import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import api, { getAvatarUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useProjectRealtime } from '../lib/useRealtime';
import { usePermission } from '../lib/permissions';
import {
  MessageSquare, Megaphone, Hash, User, Send, Paperclip, Trash2, Plus, Users, Search, Folder,
  Calendar, Download, FileText, Check, X, Pin, Sparkles, Star, Menu, Info, Globe, Bold, Image,
  Video, HelpCircle, ChevronDown, Settings, Grid, Bell, Palette, Lock, Shield, MoreVertical, CheckCheck,
  Headphones, PhoneOff, Mic, MicOff, Phone,
  Edit,
  Edit2
} from 'lucide-react';
import { Avatar } from './AppLayout';
import { useStore } from '../store/useStore';
import TaskDetail from '../components/TaskDetail';
import { usePresence } from '../lib/presence';

const ACCENT_COLORS = [
  { id: 'sapphire', label: 'Sapphire', color: '#1a73e8', light: '#d2e3fc' },
  { id: 'plum', label: 'Plum', color: '#7b1fa2', light: '#f3e5f5' },
  { id: 'magenta', label: 'Magenta', color: '#c2185b', light: '#fce4ec' },
  { id: 'coral', label: 'Coral', color: '#d84315', light: '#fbe9e7' },
  { id: 'orange', label: 'Orange', color: '#ef6c00', light: '#fff3e0' },
  { id: 'gold', label: 'Gold', color: '#f57f17', light: '#fffde7' },
  { id: 'sand', label: 'Sand', color: '#4e342e', light: '#efebe9' },
  { id: 'sage', label: 'Sage', color: '#558b2f', light: '#f1f8e9' },
  { id: 'forest', label: 'Forest', color: '#2e7d32', light: '#e8f5e9' },
  { id: 'teal', label: 'Teal', color: '#00695c', light: '#e0f2f1' },
  { id: 'ocean', label: 'Ocean', color: '#00838f', light: '#e0f7fa' },
  { id: 'lavender', label: 'Lavender', color: '#283593', light: '#e8eaf6' },
  { id: 'amethyst', label: 'Amethyst', color: '#6a1b9a', light: '#f3e5f5' },
  { id: 'charcoal', label: 'Charcoal', color: '#212121', light: '#eceff1' }
];

export default function Chat() {
  const { user, setUser } = useAuth();
  const { has } = usePermission();

  // Zustand store variables
  const {
    showTaskPanel,
    activeTaskId,
    clearActiveTask,
    messages,
    setMessages,
    addMessage,
    drafts,
    setDraft,
    loadDrafts,
    setUsers,
    unreadCounts,
    clearUnread,
    socket,
    typingUsers,
    pendingHuddleJoin,
    setPendingHuddleJoin
  } = useStore();

  const { statuses } = usePresence() || { statuses: {} };

  const [channels, setChannels] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [inputText, setInputText] = useState('');
  const [activeThreadMessage, setActiveThreadMessage] = useState(null);

  useEffect(() => {
    setActiveThreadMessage(null);
  }, [activeChannel]);
  const [mutedChannelIds, setMutedChannelIds] = useState(() => JSON.parse(localStorage.getItem('muted_channel_ids') || '[]'));
  const [showChannelDetails, setShowChannelDetails] = useState(false);
  const chatInputRef = useRef(null);
  const [extractedTasks, setExtractedTasks] = useState([]);
  const [extractingTasks, setExtractingTasks] = useState(false);
  const [selectedExtractedTasks, setSelectedExtractedTasks] = useState([]);
  const [targetTaskProject, setTargetTaskProject] = useState('');

  // WebRTC Audio Huddle States
  const [inHuddle, setInHuddle] = useState(false);
  const [huddleParticipants, setHuddleParticipants] = useState([]);
  const [incomingCall, setIncomingCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [huddleActiveChannel, setHuddleActiveChannel] = useState(null);
  const peerConnectionsRef = useRef({});
  const localStreamRef = useRef(null);

  // Adjustable Column Width States & Handlers
  const [col1Width, setCol1Width] = useState(256);
  const [col2Width, setCol2Width] = useState(320);
  const col1Ref = useRef(null);
  const col2Ref = useRef(null);

  const startResizeCol1 = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = col1Width;

    const doDrag = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.max(160, Math.min(400, startWidth + deltaX));
      setCol1Width(newWidth);
    };

    const stopDrag = () => {
      document.removeEventListener('mousemove', doDrag);
      document.removeEventListener('mouseup', stopDrag);
    };

    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', stopDrag);
  };

  const startResizeCol2 = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = col2Width;

    const doDrag = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.max(180, Math.min(450, startWidth + deltaX));
      setCol2Width(newWidth);
    };

    const stopDrag = () => {
      document.removeEventListener('mousemove', doDrag);
      document.removeEventListener('mouseup', stopDrag);
    };

    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', stopDrag);
  };

  const toggleMute = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !nextMuted;
      });
    }
    if (socket && huddleActiveChannel) {
      socket.emit('huddle:mute', { channelId: huddleActiveChannel.id, isMuted: nextMuted });
    }
  };

  const createPeerConnection = async (targetSocketId, targetUserId, isInitiator) => {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    peerConnectionsRef.current[targetSocketId] = pc;

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    pc.onicecandidate = (event) => {
      if (event.candidate && socket) {
        socket.emit('huddle:signal', { targetSocketId, signal: event.candidate });
      }
    };

    pc.ontrack = (event) => {
      setRemoteStreams(prev => ({
        ...prev,
        [targetSocketId]: event.streams[0]
      }));
    };

    if (isInitiator && socket) {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('huddle:signal', { targetSocketId, signal: offer });
      } catch (err) {
        console.error('Failed to create WebRTC offer', err);
      }
    }

    return pc;
  };

  const joinHuddle = async (chan) => {
    if (!socket) return;
    try {
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        localStreamRef.current = stream;
      } catch (err) {
        console.warn('Microphone permission denied. Running in visual simulator mode.', err);
      }

      setInHuddle(true);
      setHuddleActiveChannel(chan);
      setIsMuted(false);
      setHuddleParticipants([]);
      setRemoteStreams({});

      socket.emit('huddle:join', { channelId: chan.id });
    } catch (err) {
      console.error('Failed to join huddle', err);
    }
  };

  const leaveHuddle = () => {
    if (!socket || !huddleActiveChannel) return;

    socket.emit('huddle:leave', { channelId: huddleActiveChannel.id });

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }

    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    peerConnectionsRef.current = {};

    setInHuddle(false);
    setHuddleActiveChannel(null);
    setHuddleParticipants([]);
    setRemoteStreams({});
  };

  useEffect(() => {
    if (pendingHuddleJoin && channels.length > 0) {
      const channelToJoin = channels.find(c => c.id === pendingHuddleJoin);
      if (channelToJoin) {
        setActiveChannel(channelToJoin);
        joinHuddle(channelToJoin);
        setPendingHuddleJoin(null);
      }
    }
  }, [pendingHuddleJoin, channels]);

  useEffect(() => {
    if (!socket) return;

    const onHuddleParticipants = async (data) => {
      const { participants } = data;
      setHuddleParticipants(participants);
      for (const p of participants) {
        await createPeerConnection(p.socketId, p.userId, true);
      }
    };

    const onHuddleJoined = async (data) => {
      const { userId, socketId } = data;
      setHuddleParticipants(prev => {
        if (prev.some(p => p.socketId === socketId)) return prev;
        return [...prev, { userId, socketId }];
      });
    };

    const onHuddleLeft = (data) => {
      const { socketId } = data;
      setHuddleParticipants(prev => prev.filter(p => p.socketId !== socketId));
      if (peerConnectionsRef.current[socketId]) {
        peerConnectionsRef.current[socketId].close();
        delete peerConnectionsRef.current[socketId];
      }
      setRemoteStreams(prev => {
        const next = { ...prev };
        delete next[socketId];
        return next;
      });
    };

    const onHuddleSignal = async (data) => {
      const { senderSocketId, senderUserId, signal } = data;
      let pc = peerConnectionsRef.current[senderSocketId];
      if (!pc) {
        pc = await createPeerConnection(senderSocketId, senderUserId, false);
      }

      if (signal.type === 'offer') {
        await pc.setRemoteDescription(new RTCSessionDescription(signal));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('huddle:signal', { targetSocketId: senderSocketId, signal: answer });
      } else if (signal.type === 'answer') {
        await pc.setRemoteDescription(new RTCSessionDescription(signal));
      } else if (signal.candidate) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(signal));
        } catch (err) {
          console.error('Error adding ice candidate', err);
        }
      }
    };

    const onHuddleMuteStatus = (data) => {
      const { socketId, isMuted } = data;
      setHuddleParticipants(prev => prev.map(p => {
        if (p.socketId === socketId) {
          return { ...p, isMuted };
        }
        return p;
      }));
    };

    const onIncomingCall = (data) => {
      setIncomingCall(data);
    };

    const onCallRejected = () => {
      setIncomingCall(null);
    };

    socket.on('huddle:participants', onHuddleParticipants);
    socket.on('huddle:joined', onHuddleJoined);
    socket.on('huddle:left', onHuddleLeft);
    socket.on('huddle:signal', onHuddleSignal);
    socket.on('huddle:mute_status', onHuddleMuteStatus);
    socket.on('huddle:incoming_call', onIncomingCall);
    socket.on('huddle:call_rejected', onCallRejected);

    return () => {
      socket.off('huddle:participants', onHuddleParticipants);
      socket.off('huddle:joined', onHuddleJoined);
      socket.off('huddle:left', onHuddleLeft);
      socket.off('huddle:signal', onHuddleSignal);
      socket.off('huddle:mute_status', onHuddleMuteStatus);
      socket.off('huddle:incoming_call', onIncomingCall);
      socket.off('huddle:call_rejected', onCallRejected);
    };
  }, [socket]);

  const handleExtractTasks = async () => {
    setExtractingTasks(true);
    try {
      const { data } = await api.post('/ai/extract-tasks', { channelId: activeChannel.id });
      setExtractedTasks(data.tasks || []);
      setSelectedExtractedTasks((data.tasks || []).map((_, i) => i)); // select all by default
      if (userProjects.length > 0) {
        setTargetTaskProject(activeChannel.project_id || userProjects[0].id);
      }
    } catch (err) {
      toast.error('Failed to extract tasks from chat');
    } finally {
      setExtractingTasks(false);
    }
  };

  const handleCreateExtractedTasks = async () => {
    if (selectedExtractedTasks.length === 0) {
      toast.error('Please select at least one task');
      return;
    }
    if (!targetTaskProject) {
      toast.error('Please select a target project board');
      return;
    }

    try {
      for (const idx of selectedExtractedTasks) {
        const t = extractedTasks[idx];
        await api.post('/tasks', {
          title: t.title,
          description: t.description,
          project_id: targetTaskProject
        });
      }
      toast.success(`Successfully created ${selectedExtractedTasks.length} tasks on board!`);
      setShowSummaryModal(false);
      setExtractedTasks([]);
    } catch (err) {
      toast.error('Failed to create tasks on board');
    }
  };
  const insertMarkdown = (syntax) => {
    const el = chatInputRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = inputText;
    const selected = text.slice(start, end);
    let replacement = '';
    if (syntax === 'bold') replacement = `**${selected || 'bold'}**`;
    else if (syntax === 'italic') replacement = `_${selected || 'italic'}_`;
    else if (syntax === 'code') replacement = `\`${selected || 'code'}\``;
    else if (syntax === 'strike') replacement = `~${selected || 'strikethrough'}~`;

    setInputText(text.slice(0, start) + replacement + text.slice(end));

    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + replacement.length, start + replacement.length);
    }, 50);
  };
  const [uploading, setUploading] = useState(false);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinnedList, setShowPinnedList] = useState(false);
  const [showNewChatDropdown, setShowNewChatDropdown] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileAvatarUrl, setProfileAvatarUrl] = useState('');
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const profileFileInputRef = useRef(null);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showEditChanModal, setShowEditChanModal] = useState(false);
  const [editChanName, setEditChanName] = useState('');
  const [editChanAvatarUrl, setEditChanAvatarUrl] = useState('');
  const [uploadingChanAvatar, setUploadingChanAvatar] = useState(false);
  const chanAvatarFileInputRef = useRef(null);
  const [channelMembers, setChannelMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [dmSortMode, setDmSortMode] = useState('recent');
  const [showDmSortMenu, setShowDmSortMenu] = useState(false);
  const [groupSortMode, setGroupSortMode] = useState('recent');
  const [showGroupSortMenu, setShowGroupSortMenu] = useState(false);
  const [channelSortMode, setChannelSortMode] = useState('alphabetical');
  const [showChannelSortMenu, setShowChannelSortMenu] = useState(false);
  const [projectSortMode, setProjectSortMode] = useState('alphabetical');
  const [showProjectSortMenu, setShowProjectSortMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifs, setGifs] = useState([]);
  const [gifSearchQuery, setGifSearchQuery] = useState('');
  const [loadingGifs, setLoadingGifs] = useState(false);
  const [showFormattingHelp, setShowFormattingHelp] = useState(false);
  const [activeMenuMessageId, setActiveMenuMessageId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const messagesContainerRef = useRef(null);
  const [showNewMessageBanner, setShowNewMessageBanner] = useState(false);
  const [channelUnreadCount, setChannelUnreadCount] = useState(0);
  const [companyDepartments, setCompanyDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [composeSuggestion, setComposeSuggestion] = useState('');

  // AI states
  const [summarizing, setSummarizing] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [smartReplies, setSmartReplies] = useState([]);

  const [messageInfo, setMessageInfo] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const handleShowMessageInfo = async (msg) => {
    try {
      const { data } = await api.get(`/chat/messages/${msg.id}/info`);
      setMessageInfo(data);
      setShowInfoModal(true);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to fetch message details');
    }
  };

  // Chat settings state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState('notifications');
  const [chatSettings, setChatSettings] = useState(() => {
    const saved = localStorage.getItem('chat_settings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing chat settings:', e);
      }
    }
    return {
      allowNotifications: true,
      allowReactions: true,
      notificationSound: 'tones',
      emailNotifications: true,
      smartCompose: true,
      smartReply: true,
      autoCorrect: true,
      conversationSummary: true,
      liveMarkdown: true,
      theme: 'light',
      density: 'comfortable',
      accentColor: 'sapphire'
    };
  });

  const [browserNotificationPermission, setBrowserNotificationPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  const requestBrowserNotificationPermission = async () => {
    if (typeof Notification !== 'undefined') {
      const result = await Notification.requestPermission();
      setBrowserNotificationPermission(result);
    }
  };

  useEffect(() => {
    localStorage.setItem('chat_settings', JSON.stringify(chatSettings));
  }, [chatSettings]);

  // Task suggest autocomplete state
  const [tasksList, setTasksList] = useState([]);
  const [showTaskSuggest, setShowTaskSuggest] = useState(false);
  const [taskFilterQuery, setTaskFilterQuery] = useState('');

  // Modal State for new channel
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newChanName, setNewChanName] = useState('');
  const [newChanType, setNewChanType] = useState('company'); // company, announcement, department, project
  const [selectedUserIds, setSelectedUserIds] = useState([]); // for DMs

  // Convert Message to Task States
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertMsg, setConvertMsg] = useState(null);
  const [convertProjId, setConvertProjId] = useState('');
  const [convertListId, setConvertListId] = useState('');
  const [convertTitle, setConvertTitle] = useState('');
  const [convertDesc, setConvertDesc] = useState('');
  const [convertLists, setConvertLists] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  const [convertBusy, setConvertBusy] = useState(false);

  // Google Chat Layout Redesign State
  const [activeShortcut, setActiveShortcut] = useState('home'); // home, mentions, starred
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [userPresenceStatus, setUserPresenceStatus] = useState('active'); // active, away, do_not_disturb
  const [showUserSuggest, setShowUserSuggest] = useState(false);
  const [userFilterQuery, setUserFilterQuery] = useState('');

  const [starredMessages, setStarredMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('tm_starred_messages_data');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const handleToggleStarMessage = (msg) => {
    setStarredMessages(prev => {
      const exists = prev.some(m => m.id === msg.id);
      let next;
      if (exists) {
        next = prev.filter(m => m.id !== msg.id);
      } else {
        next = [...prev, {
          id: msg.id,
          sender_name: msg.sender_name || 'User',
          message_text: msg.message_text,
          created_at: msg.created_at || new Date().toISOString(),
          channel_id: activeChannel?.id,
          channel_name: activeChannel?.name || 'General'
        }];
      }
      localStorage.setItem('tm_starred_messages_data', JSON.stringify(next));
      return next;
    });
  };

  const handlePresenceChange = async (status) => {
    setUserPresenceStatus(status);
    try {
      await api.post('/chat/users/status', { presence_status: status });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (user) {
      setProfileName(user.full_name || '');
      setProfileAvatarUrl(user.avatar_url || '');
    }
  }, [user]);

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingProfile(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post('/uploads/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfileAvatarUrl(data.url);
      setUser({ ...user, avatar_url: data.url });
    } catch (err) {
      alert(err.response?.data?.error || 'Profile photo upload failed');
    } finally {
      setUploadingProfile(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!profileName.trim()) return;
    try {
      const { data } = await api.put('/auth/profile', {
        full_name: profileName,
        avatar_url: profileAvatarUrl || null
      });
      setUser(data.user);
      setShowProfileModal(false);
      alert('Profile updated successfully!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update profile');
    }
  };

  const loadChannelMembers = async () => {
    if (!activeChannel) return;
    setLoadingMembers(true);
    try {
      const { data } = await api.get(`/chat/channels/${activeChannel.id}/members`);
      setChannelMembers(data.members || []);
    } catch (e) {
      console.error('Error loading channel members:', e);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (showMembersModal && activeChannel) {
      loadChannelMembers();
    }
  }, [showMembersModal, activeChannel]);

  useEffect(() => {
    if (socket && activeChannel?.id) {
      socket.emit('message:read', { channelId: activeChannel.id });
    }
  }, [socket, activeChannel?.id]);

  const handleAddMembers = async (uids) => {
    if (!activeChannel || !uids.length) return;
    try {
      await api.post(`/chat/channels/${activeChannel.id}/members`, { user_ids: uids });
      await loadChannelMembers();
      await loadChannels();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add members');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!activeChannel) return;
    if (!confirm('Are you sure you want to remove this member?')) return;
    try {
      await api.delete(`/chat/channels/${activeChannel.id}/members/${userId}`);
      await loadChannelMembers();
      await loadChannels();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleStartDirectMessage = async (targetUser) => {
    try {
      setShowMembersModal(false);
      const { data } = await api.post('/chat/channels', {
        name: targetUser.full_name,
        type: 'direct',
        invite_user_ids: [targetUser.id]
      });
      await loadChannels();
      setActiveChannel(data.channel);
      setActiveShortcut('home');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to start direct message');
    }
  };

  const handleChanPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingChanAvatar(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditChanAvatarUrl(data.url);
    } catch (err) {
      alert(err.response?.data?.error || 'Channel photo upload failed');
    } finally {
      setUploadingChanAvatar(false);
    }
  };

  const handleUpdateChannel = async (e) => {
    e.preventDefault();
    if (!activeChannel || !editChanName.trim()) return;
    try {
      const { data } = await api.put(`/chat/channels/${activeChannel.id}`, {
        name: editChanName.trim(),
        avatar_url: editChanAvatarUrl || null
      });
      setActiveChannel(data.channel);
      setShowEditChanModal(false);
      await loadChannels();
      alert('Channel settings updated successfully!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update channel');
    }
  };

  const loadTrendingGifs = async () => {
    setLoadingGifs(true);
    try {
      const response = await fetch('https://api.giphy.com/v1/gifs/trending?api_key=dc6zaTOxFJmzC&limit=10');
      const data = await response.json();
      setGifs(data.data || []);
    } catch (e) {
      console.error('Error loading trending gifs:', e);
      setGifs([
        { id: '1', title: 'Thumbs Up', images: { fixed_height: { url: 'https://media.giphy.com/media/tIeCLkB8geYtW/giphy.gif' }, fixed_height_small: { url: 'https://media.giphy.com/media/tIeCLkB8geYtW/giphy.gif' } } },
        { id: '2', title: 'Applause', images: { fixed_height: { url: 'https://media.giphy.com/media/3o7qDEq2bMbcbPRVP2/giphy.gif' }, fixed_height_small: { url: 'https://media.giphy.com/media/3o7qDEq2bMbcbPRVP2/giphy.gif' } } },
        { id: '3', title: 'Celebrate', images: { fixed_height: { url: 'https://media.giphy.com/media/kyLYXonQpk2MsNeg0z/giphy.gif' }, fixed_height_small: { url: 'https://media.giphy.com/media/kyLYXonQpk2MsNeg0z/giphy.gif' } } },
        { id: '4', title: 'Dance', images: { fixed_height: { url: 'https://media.giphy.com/media/l41Yh1olOKd1Tgbw4/giphy.gif' }, fixed_height_small: { url: 'https://media.giphy.com/media/l41Yh1olOKd1Tgbw4/giphy.gif' } } }
      ]);
    } finally {
      setLoadingGifs(false);
    }
  };

  const searchGifs = async (query) => {
    if (!query.trim()) {
      loadTrendingGifs();
      return;
    }
    setLoadingGifs(true);
    try {
      const response = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=dc6zaTOxFJmzC&q=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();
      setGifs(data.data || []);
    } catch (e) {
      console.error('Error searching gifs:', e);
    } finally {
      setLoadingGifs(false);
    }
  };

  const handleSendGif = async (url, title) => {
    if (!activeChannel) return;
    try {
      setShowGifPicker(false);
      const payload = {
        message_text: `Sent GIF: ${title}`,
        attachment_url: url,
        attachment_name: 'animated.gif',
        attachment_size: 102400
      };

      const { data } = await api.post(`/chat/channels/${activeChannel.id}/messages`, payload);
      addMessage(data.sentMessage);

      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send GIF');
    }
  };

  // File attachments state
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Load task list for autocomplete, load drafts, load departments, and load projects on mount
  useEffect(() => {
    loadDrafts();
    api.get('/tasks').then(({ data }) => {
      setTasksList(data.tasks || []);
    }).catch(e => console.error('Error loading autocomplete tasks', e));
    api.get('/settings/departments').then(({ data }) => {
      setCompanyDepartments(data.departments || []);
    }).catch(e => console.error('Error loading departments', e));
    api.get('/projects').then(({ data }) => {
      setUserProjects(data.projects || []);
    }).catch(e => console.error('Error loading projects', e));
  }, []);

  useEffect(() => {
    // Moved theme handling to global ThemeContext
  }, []);

  // Update active channel globally in store
  useEffect(() => {
    useStore.setState({ activeChannel });
    if (activeChannel) {
      const counts = useStore.getState().unreadCounts;
      const count = counts[activeChannel.id] || 0;
      setChannelUnreadCount(count);
      clearUnread(activeChannel.id);

      // Load saved draft for this channel
      const savedDraft = drafts[activeChannel.id] || '';
      setInputText(savedDraft);
    } else {
      setChannelUnreadCount(0);
    }
  }, [activeChannel, drafts, clearUnread]);

  // Load channels and users
  const loadChannels = async () => {
    try {
      const { data } = await api.get('/chat/channels');
      setChannels(data.channels);
      setCompanyUsers(data.company_users);
      setUsers(data.company_users); // cache globally in store
      // Auto-select first company/general channel
      if (data.channels.length > 0 && !activeChannel) {
        const general = data.channels.find(c => c.type === 'company' && c.name === 'General') || data.channels[0];
        setActiveChannel(general);
      }
    } catch (e) {
      console.error('Error loading channels', e);
    }
  };

  const loadPinnedMessages = async () => {
    if (!activeChannel) return;
    try {
      const { data } = await api.get(`/chat/channels/${activeChannel.id}/pinned`);
      setPinnedMessages(data.pinned_messages || []);
    } catch (e) {
      console.error('Error loading pinned messages', e);
    }
  };

  useEffect(() => {
    loadChannels();
  }, []);

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 200;
    if (isAtBottom) {
      setShowNewMessageBanner(false);
    }
  };

  const scrollToBottom = (force = false) => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 200;

    if (force || isAtBottom) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
      setShowNewMessageBanner(false);
    } else {
      setShowNewMessageBanner(true);
    }
  };

  // Load chat history when active channel changes
  useEffect(() => {
    if (!activeChannel) return;
    setMessages([]);
    loadPinnedMessages();
    api.get(`/chat/channels/${activeChannel.id}/messages`).then(({ data }) => {
      setMessages(data.messages);
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }).catch(e => console.error('Error loading messages', e));
  }, [activeChannel]);

  // Handle scrolling when messages state changes
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      const isMyMessage = lastMsg && user && (lastMsg.senderId === user.id || lastMsg.sender_id === user.id || lastMsg.senderName === user.full_name);
      scrollToBottom(isMyMessage);
    }
  }, [messages]);

  // Fetch Smart Replies suggestions on new messages
  const loadSmartReplies = async () => {
    if (!activeChannel) return;
    try {
      const { data } = await api.post('/chat/ai/smart-replies', { channelId: activeChannel.id });
      setSmartReplies(data.replies || []);
    } catch {
      setSmartReplies([]);
    }
  };

  useEffect(() => {
    loadSmartReplies();
  }, [activeChannel, messages]);

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!inputText.trim() && !attachment) return;

    if (isTypingRef.current && socket && activeChannel) {
      isTypingRef.current = false;
      socket.emit('typing:stop', { channelId: activeChannel.id });
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    // Slash command parsing
    if (inputText.trim().startsWith('/')) {
      const parts = inputText.trim().split(' ');
      const command = parts[0].toLowerCase();
      const argsText = parts.slice(1).join(' ');

      if (command === '/todo') {
        if (!argsText.trim()) {
          toast.error('Usage: /todo [task title]');
          setInputText('');
          return;
        }
        const targetProjectId = activeChannel.project_id || (userProjects[0] && userProjects[0].id);
        if (!targetProjectId) {
          toast.error('No project found in workspace to assign this task.');
          setInputText('');
          return;
        }
        try {
          await api.post('/tasks', {
            title: argsText.trim(),
            description: `Created via chat command in channel: #${activeChannel.name}`,
            project_id: targetProjectId
          });
          toast.success(`Task "${argsText.trim()}" created successfully!`);
        } catch (err) {
          toast.error('Failed to create task via command');
        }
        setInputText('');
        return;
      }

      if (command === '/mute') {
        const isMuted = mutedChannelIds.includes(activeChannel.id);
        let updatedMuted = [];
        if (isMuted) {
          updatedMuted = mutedChannelIds.filter(id => id !== activeChannel.id);
          toast.success(`Unmuted channel #${activeChannel.name}`);
        } else {
          updatedMuted = [...mutedChannelIds, activeChannel.id];
          toast.success(`Muted channel #${activeChannel.name}`);
        }
        setMutedChannelIds(updatedMuted);
        localStorage.setItem('muted_channel_ids', JSON.stringify(updatedMuted));
        setInputText('');
        return;
      }

      if (command === '/ai') {
        handleSummarize();
        setInputText('');
        return;
      }
    }

    try {
      const payload = { message_text: inputText };
      if (attachment) {
        payload.attachment_url = attachment.url;
        payload.attachment_name = attachment.name;
        payload.attachment_size = attachment.size;
      }

      // Clear draft
      setDraft(activeChannel.id, '');
      setInputText('');
      setAttachment(null);

      const { data } = await api.post(`/chat/channels/${activeChannel.id}/messages`, payload);
      addMessage(data.sentMessage);

      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      console.error('Error sending message', err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachment(data);
    } catch (err) {
      alert(err.response?.data?.error || 'File upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMessage = async (msgId) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    try {
      await api.delete(`/chat/messages/${msgId}`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } catch (err) {
      console.error('Failed to delete message', err);
    }
  };

  const handleUpdateMessage = async (msgId, newText) => {
    if (!newText.trim()) return;
    try {
      await api.put(`/chat/messages/${msgId}`, { message_text: newText.trim() });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, message_text: newText.trim(), messageText: newText.trim() } : m));
      setEditingMessageId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to edit message');
    }
  };

  const handleToggleReaction = async (messageId, emoji) => {
    try {
      const { data } = await api.post(`/chat/messages/${messageId}/react`, { emoji });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: data.reactions } : m));
    } catch (err) {
      console.error('Failed to toggle reaction', err);
    }
  };

  const handleTogglePin = async (messageId) => {
    try {
      const { data } = await api.put(`/chat/messages/${messageId}/pin`);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_pinned: data.is_pinned } : m));
      loadPinnedMessages();
    } catch (err) {
      console.error('Failed to pin/unpin message', err);
    }
  };

  const handleInputChange = (e) => {
    let val = e.target.value;

    // 1. Auto-Correct (only when space is typed)
    if (chatSettings.autoCorrect && val.endsWith(' ')) {
      const words = val.trim().split(/\s+/);
      if (words.length > 0) {
        const lastWord = words[words.length - 1].toLowerCase();
        const corrections = {
          'teh': 'the',
          'dont': "don't",
          'cant': "can't",
          'wont': "won't",
          'recieve': 'receive',
          'seestem': 'system',
          'seeting': 'setting',
          'chanal': 'channel',
          'pertuculer': 'particular',
          'mulitpale': 'multiple',
          'yuo': 'you',
          'realy': 'really',
          'writting': 'writing',
          'tommorow': 'tomorrow'
        };
        if (corrections[lastWord]) {
          words[words.length - 1] = corrections[lastWord];
          val = words.join(' ') + ' ';
        }
      }
    }

    // 2. Smart Compose (proactive writing suggestions)
    if (chatSettings.smartCompose) {
      const cleanVal = val.toLowerCase().trim();
      const COMPOSITIONS = {
        'how are': ' you?',
        'please let': ' me know.',
        'thank you': ' so much!',
        "let's meet": ' tomorrow.',
        'sounds good': ' to me.',
        'i will check': ' that out.',
        'keep me': ' posted.'
      };
      let found = '';
      for (const [key, sug] of Object.entries(COMPOSITIONS)) {
        if (cleanVal.endsWith(key)) {
          found = sug;
          break;
        }
      }
      setComposeSuggestion(found);
    } else {
      setComposeSuggestion('');
    }

    setInputText(val);
    if (activeChannel) {
      setDraft(activeChannel.id, val);
    }

    if (!isTypingRef.current && socket && activeChannel) {
      isTypingRef.current = true;
      socket.emit('typing:start', { channelId: activeChannel.id });
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      if (isTypingRef.current && socket && activeChannel) {
        isTypingRef.current = false;
        socket.emit('typing:stop', { channelId: activeChannel.id });
      }
    }, 2000);

    const words = val.split(' ');
    const lastWord = words[words.length - 1];
    if (lastWord.startsWith('#')) {
      setTaskFilterQuery(lastWord.slice(1));
      setShowTaskSuggest(true);
      setShowUserSuggest(false);
    } else if (lastWord.startsWith('@')) {
      setUserFilterQuery(lastWord.slice(1));
      setShowUserSuggest(true);
      setShowTaskSuggest(false);
    } else {
      setShowTaskSuggest(false);
      setShowUserSuggest(false);
    }
  };

  const handleSelectTaskReference = (task) => {
    const words = inputText.split(' ');
    words[words.length - 1] = `[#${task.title}](task:${task.id})`;
    const newVal = words.join(' ') + ' ';
    setInputText(newVal);
    if (activeChannel) {
      setDraft(activeChannel.id, newVal);
    }
    setShowTaskSuggest(false);
  };

  const handleSelectUserReference = (selectedUser) => {
    const words = inputText.split(' ');
    words[words.length - 1] = `@${selectedUser.name}`;
    const newVal = words.join(' ') + ' ';
    setInputText(newVal);
    if (activeChannel) {
      setDraft(activeChannel.id, newVal);
    }
    setShowUserSuggest(false);
  };

  const filteredTasks = tasksList.filter(t =>
    t.title.toLowerCase().includes(taskFilterQuery.toLowerCase())
  ).slice(0, 5);

  const filteredUsers = companyUsers.filter(u =>
    u.name.toLowerCase().includes(userFilterQuery.toLowerCase())
  ).slice(0, 5);

  const handleOpenConvertModal = async (msg) => {
    setConvertMsg(msg);
    setConvertTitle(`From Chat: ${msg.message_text ? msg.message_text.slice(0, 40) : 'File attachment'}`);
    setConvertDesc(`${msg.message_text || ''}\n\n---\nRef Chat Discussion in #${activeChannel.name}:\n[Sender: ${msg.sender_name} | Date: ${new Date(msg.created_at).toLocaleString()}]`);
    setShowConvertModal(true);

    try {
      const { data } = await api.get('/projects');
      setUserProjects(data.projects || []);
      if (data.projects.length > 0) {
        setConvertProjId(data.projects[0].id);
      }
    } catch (e) {
      console.error('Error loading projects for conversion', e);
    }
  };

  const handleAICreateTask = async (msg) => {
    setConvertBusy(true);
    try {
      const { data } = await api.post('/chat/ai/create-task', { channelId: activeChannel.id });
      setConvertMsg(msg);
      setConvertTitle(data.title);
      setConvertDesc(data.description);

      const projectsRes = await api.get('/projects');
      setUserProjects(projectsRes.data.projects || []);
      if (projectsRes.data.projects.length > 0) {
        setConvertProjId(projectsRes.data.projects[0].id);
      }
      setShowConvertModal(true);
    } catch (err) {
      alert('Failed to generate AI task suggestion');
    } finally {
      setConvertBusy(false);
    }
  };

  const handleSummarize = async () => {
    setSummarizing(true);
    setExtractedTasks([]);
    try {
      const { data } = await api.post('/chat/ai/summarize', { channelId: activeChannel.id });
      setSummaryText(data.summary);
      setShowSummaryModal(true);
    } catch (err) {
      alert('Failed to generate AI thread summary');
    } finally {
      setSummarizing(false);
    }
  };

  useEffect(() => {
    if (!convertProjId) return;
    api.get(`/lists/project/${convertProjId}`).then(({ data }) => {
      setConvertLists(data.lists || []);
      if (data.lists.length > 0) {
        setConvertListId(data.lists[0].id);
      } else {
        setConvertListId('');
      }
    }).catch(e => console.error(e));
  }, [convertProjId]);

  const handleConvertSubmit = async (e) => {
    e.preventDefault();
    if (!convertProjId || !convertListId || !convertTitle.trim()) return;
    setConvertBusy(true);
    try {
      await api.post('/tasks', {
        project_id: convertProjId,
        list_id: convertListId,
        title: convertTitle.trim(),
        description: convertDesc,
        priority: 'medium'
      });
      setShowConvertModal(false);
      alert('Task successfully created from chat discussion!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create task');
    } finally {
      setConvertBusy(false);
    }
  };




  const handleOpenCreateModal = async (initialType) => {
    setNewChanType(initialType);
    setSelectedUserIds([]);
    setNewChanName('');
    setMemberSearchQuery('');
    setShowCreateModal(true);

    try {
      const deptsRes = await api.get('/settings/departments');
      setCompanyDepartments(deptsRes.data.departments || []);
    } catch (e) {
      console.error('Error fetching departments', e);
    }

    try {
      const projsRes = await api.get('/projects');
      setUserProjects(projsRes.data.projects || []);
    } catch (e) {
      console.error('Error fetching projects', e);
    }
  };

  const handleCreateChannel = async (e) => {
    e.preventDefault();
    try {
      let channelName = newChanName;
      if (newChanType === 'direct' && selectedUserIds.length > 0) {
        const otherUser = companyUsers.find(u => u.id === selectedUserIds[0]);
        if (otherUser) {
          channelName = otherUser.full_name;
        }
      } else if (newChanType === 'group') {
        if (!newChanName.trim()) {
          const names = selectedUserIds.map(id => companyUsers.find(u => u.id === id)?.name || companyUsers.find(u => u.id === id)?.full_name).filter(Boolean);
          channelName = names.slice(0, 3).join(', ') + (names.length > 3 ? '...' : '');
        }
      }

      const payload = {
        name: channelName,
        type: newChanType,
        invite_user_ids: selectedUserIds,
        department_id: newChanType === 'department' ? selectedDepartmentId : null,
        project_id: newChanType === 'project' ? selectedProjectId : null
      };
      const { data } = await api.post('/chat/channels', payload);
      setShowCreateModal(false);
      setNewChanName('');
      setSelectedUserIds([]);
      setSelectedDepartmentId('');
      setSelectedProjectId('');
      await loadChannels();
      setActiveChannel(data.channel);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create channel');
    }
  };

  const toggleSelectUser = (uid) => {
    setSelectedUserIds(prev => {
      if (newChanType === 'direct') {
        return prev.includes(uid) ? [] : [uid];
      } else {
        return prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid];
      }
    });
  };

  const getDMName = (channel) => {
    if (!companyUsers.length) return 'Direct Message';
    if (channel.dm_recipient_names) return channel.dm_recipient_names;
    return channel.name === 'Direct Message' ? 'Private Chat' : channel.name;
  };

  const sortChannels = (channelList, sortMode) => {
    return [...channelList].sort((a, b) => {
      if (sortMode === 'alphabetical') {
        const nameA = a.type === 'direct' ? getDMName(a) : (a.name || '');
        const nameB = b.type === 'direct' ? getDMName(b) : (b.name || '');
        return nameA.localeCompare(nameB);
      } else {
        const timeA = new Date(a.last_message_time || a.created_at).getTime();
        const timeB = new Date(b.last_message_time || b.created_at).getTime();
        return timeB - timeA;
      }
    });
  };

  // Group channels by category
  const announcementChannels = channels.filter(c => c.type === 'announcement');
  const regularChannels = sortChannels(channels.filter(c => c.type === 'company' || c.type === 'department'), channelSortMode);
  const projectChannels = sortChannels(channels.filter(c => c.type === 'project'), projectSortMode);
  const directChannels = sortChannels(channels.filter(c => c.type === 'direct'), dmSortMode);
  const groupChannels = sortChannels(channels.filter(c => c.type === 'group'), groupSortMode);

  const formatSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const renderChannelAvatar = (c) => {
    if (c.avatar_url) {
      return (
        <div
          className="w-9 h-9 rounded-full shadow-sm flex-shrink-0"
          style={{ background: `url(${getAvatarUrl(c.avatar_url)}) center/cover` }}
        />
      );
    }
    if (c.type === 'direct') {
      const otherUser = companyUsers.find(u => u.name === c.name || u.full_name === c.name || getDMName(c) === u.full_name);
      if (otherUser) {
        return <Avatar user={otherUser} size={36} showStatus={true} />;
      }
      return <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-sm">💬</div>;
    }
    if (c.type === 'group') {
      return <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm shadow-sm relative">👥</div>;
    }

    const name = c.name || 'Space';
    const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    const bgColors = ['#0079BF', '#61BD4F', '#F2D600', '#FF9F1A', '#EB5A46', '#C377E0'];
    const idx = name.charCodeAt(0) % bgColors.length;
    const color = bgColors[idx];

    return (
      <div style={{ backgroundColor: color }} className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm relative">
        {c.type === 'announcement' ? '📢' : initials}
      </div>
    );
  };

  const getThreadDate = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  };

  const allHomeChannels = [...announcementChannels, ...regularChannels, ...projectChannels, ...directChannels, ...groupChannels];

  const filteredHomeChannels = allHomeChannels.filter(c => {
    const name = ['direct', 'group'].includes(c.type) ? getDMName(c) : c.name;
    const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesUnread = !unreadOnly || (unreadCounts[c.id] > 0);
    return matchesSearch && matchesUnread;
  });

  const mentionMessages = messages.filter(m => {
    const text = m.message_text || '';
    return user && text.toLowerCase().includes(`@${user.full_name.toLowerCase()}`);
  });

  return (
    <>
      {/* Dynamic Theme & Accent CSS Injection */}
      <style>{`
        :root {
          --primary-accent: ${ACCENT_COLORS.find(c => c.id === chatSettings.accentColor)?.color || '#1a73e8'};
          --primary-accent-light: ${ACCENT_COLORS.find(c => c.id === chatSettings.accentColor)?.light || '#d2e3fc'};
        }
        .theme-dark {
          --primary-accent-light: ${ACCENT_COLORS.find(c => c.id === chatSettings.accentColor)?.color || '#1a73e8'}33 !important;
        }
        
        /* Apply accent color to custom elements */
        .text-blue-600, .text-blue-700, .text-blue-500 {
          color: var(--primary-accent) !important;
        }
        .bg-blue-600, .bg-blue-500 {
          background-color: var(--primary-accent) !important;
        }
        .bg-blue-50, .bg-blue-50\\/20, .bg-blue-50\\/50, .bg-blue-50\\/70, .bg-\\[\\#e8f0fe\\] {
          background-color: var(--primary-accent-light) !important;
        }
        .hover\:bg-blue-700:hover, .hover\:bg-blue-600:hover {
          filter: brightness(0.9);
        }
        .focus\:border-blue-500:focus, .border-blue-500 {
          border-color: var(--primary-accent) !important;
        }
        .text-\[\#185abc\], .text-blue-800 {
          color: var(--primary-accent) !important;
        }
        .bg-\[\#185abc\] {
          background-color: var(--primary-accent) !important;
        }
        .border-blue-150 {
          border-color: var(--primary-accent-light) !important;
        }
        
        /* Hide Scrollbars in Chat */
        .theme-dark ::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
        .theme-dark {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
      `}</style>
      <div className={`flex h-[calc(100vh-45px)] w-full overflow-hidden bg-white text-slate-800 select-none font-sans density-${chatSettings.density}`}>

        {/* Tab Rail (Tab Panel for Home, Mentions, Starred) */}
        <div className="w-[64px] bg-slate-100 border-r flex flex-col items-center py-4 justify-between flex-shrink-0" style={{ borderColor: '#e0e0e0' }}>
          <div className="flex flex-col items-center gap-6 w-full">
            {/* Logo / Chat icon at top */}
            {/* <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white mb-2 shadow-md">
              <MessageSquare size={20} strokeWidth={2.5} />
            </div> */}

            {/* Tabs */}
            <div className="flex flex-col items-center gap-4 w-full">
              {/* Home Tab */}
              <button
                onClick={() => setActiveShortcut('home')}
                className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center transition-all duration-200 relative group ${activeShortcut === 'home' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200 hover:text-slate-800'}`}
                title="Home"
                data-testid="tab-home"
              >
                <MessageSquare size={20} strokeWidth={activeShortcut === 'home' ? 2.5 : 2} />
                <span className="text-[9px] mt-1 font-semibold">Home</span>
              </button>

              {/* Mentions Tab */}
              <button
                onClick={() => setActiveShortcut('mentions')}
                className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center transition-all duration-200 relative group ${activeShortcut === 'mentions' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200 hover:text-slate-800'}`}
                title="Mentions"
                data-testid="tab-mentions"
              >
                <span className="text-base font-bold leading-none">@</span>
                <span className="text-[9px] mt-0.5 font-semibold">Mentions</span>
              </button>

              {/* Starred Tab */}
              <button
                onClick={() => setActiveShortcut('starred')}
                className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center transition-all duration-200 relative group ${activeShortcut === 'starred' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200 hover:text-slate-800'}`}
                title="Starred"
                data-testid="tab-starred"
              >
                <Star size={18} className={activeShortcut === 'starred' ? 'fill-white' : ''} />
                <span className="text-[9px] mt-1 font-semibold">Starred</span>
              </button>
            </div>
          </div>
        </div>

        {/* Column 1: Far-Left Sidebar Navigation (Google Chat Rail) */}
        <div
          ref={col1Ref}
          style={{ width: `${col1Width}px`, borderColor: '#e0e0e0', display: activeShortcut === 'home' ? 'flex' : 'none' }}
          className="bg-slate-50 border-r flex flex-col justify-between flex-shrink-0"
        >
          <div className="flex flex-col gap-4 overflow-y-auto flex-1 py-4">

            {/* Header Row */}
            <div className="px-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-bold text-lg text-slate-800 tracking-tight flex items-center gap-1.5">
                  <MessageSquare className="text-blue-600 stroke-[2.5]" size={18} />
                  Chat
                </span>
              </div>
            </div>

            {/* New Chat Button & Dropdown */}
            <div style={{ display: 'none' }} className="px-4 relative">
              <button
                onClick={() => setShowNewChatDropdown(v => !v)}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-100 hover:bg-blue-200 text-blue-700 font-bold rounded-full shadow-sm text-sm border border-blue-200 transition-all duration-150"
                data-testid="new-chat-btn"
              >
                <Plus size={16} />
                New chat
              </button>

              {showNewChatDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowNewChatDropdown(false)}
                  />

                  <div className="absolute left-4 right-4 mt-2 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-20 animate-fade-in text-xs font-semibold">
                    <button
                      onClick={() => {
                        handleOpenCreateModal('direct');
                        setShowNewChatDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-slate-700 hover:text-slate-900 transition-colors text-left"
                    >
                      <User size={14} className="text-slate-400" />
                      <span>Direct chat</span>
                    </button>

                    <button
                      onClick={() => {
                        handleOpenCreateModal('group');
                        setShowNewChatDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-slate-700 hover:text-slate-900 transition-colors text-left"
                    >
                      <Users size={14} className="text-slate-400" />
                      <span>New group chat</span>
                    </button>

                    <button
                      onClick={() => {
                        handleOpenCreateModal('company');
                        setShowNewChatDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-slate-700 hover:text-slate-900 transition-colors text-left"
                    >
                      <Hash size={14} className="text-slate-400" />
                      <span>New Channel</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Shortcuts */}
            <div style={{ display: 'none' }} className="flex flex-col">
              <button
                className={`flex items-center gap-3 px-6 py-2 text-sm font-semibold transition-all ${activeShortcut === 'home' ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600' : 'text-slate-700 hover:bg-slate-100'}`}
              >
                <MessageSquare size={16} />
                Home
              </button>
              {/* <button

                onClick={() => setActiveShortcut('home')}
                className={`flex items-center gap-3 px-6 py-2 text-sm font-semibold transition-all ${activeShortcut === 'home' ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600' : 'text-slate-700 hover:bg-slate-100'}`}
              >
                <MessageSquare size={16} />
                Home
              </button> */}
              <button
                onClick={() => setActiveShortcut('mentions')}
                className={`flex items-center gap-3 px-6 py-2 text-sm font-semibold transition-all ${activeShortcut === 'mentions' ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600' : 'text-slate-700 hover:bg-slate-100'}`}
              >
                <span className="font-bold text-sm">@</span>
                Mentions
              </button>
              <button
                onClick={() => setActiveShortcut('starred')}
                className={`flex items-center gap-3 px-6 py-2 text-sm font-semibold transition-all ${activeShortcut === 'starred' ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600' : 'text-slate-700 hover:bg-slate-100'}`}
              >
                <Star size={16} className={activeShortcut === 'starred' ? 'fill-blue-600 text-blue-600' : ''} />
                Starred
              </button>
            </div>

            {/* 1. Announcement Channels */}
            <div className="px-4 pt-2">
              <div className="flex justify-between items-center mb-1 px-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Megaphone size={10} /> Announcement Channels
                </span>
              </div>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {announcementChannels.length > 0 ? (
                  announcementChannels.map(c => {
                    const isSelected = activeChannel?.id === c.id && activeShortcut === 'home';
                    return (
                      <button
                        key={c.id}
                        onClick={() => { setActiveShortcut('home'); setActiveChannel(c); }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-between transition-colors ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-100 text-slate-700'}`}
                      >
                        <span className="truncate flex items-center gap-2 flex-1">
                          {renderChannelAvatar(c)}
                          <span className="truncate">{c.name}</span>
                          {drafts[c.id] && drafts[c.id].trim().length > 0 && (
                            <span className="text-[7.5px] bg-slate-250 text-slate-500 font-black px-1 py-0.2 rounded scale-90" title="Unsent Draft">DRAFT</span>
                          )}
                        </span>
                        {unreadCounts[c.id] > 0 && <span className="text-[9px] bg-red-500 text-white font-bold px-1.5 py-0.5 rounded-full">{unreadCounts[c.id]}</span>}
                      </button>
                    );
                  })
                ) : (
                  <div className="text-[10px] text-slate-400 px-2 py-1 italic">No announcements</div>
                )}
              </div>
            </div>

            {/* 2. Direct Messages Section */}
            <div className="px-4 pt-2">
              <div className="flex justify-between items-center mb-1 px-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <User size={10} /> Direct Messages
                </span>
                <div className="flex items-center gap-1">
                  <div className="relative">
                    <button
                      onClick={() => setShowDmSortMenu(v => !v)}
                      className="p-0.5 hover:bg-slate-200 rounded text-slate-500 transition-colors"
                      title="Section options"
                    >
                      <MoreVertical size={12} />
                    </button>
                    {showDmSortMenu && (
                      <>
                        <div className="fixed inset-0 z-30 cursor-default" onClick={() => setShowDmSortMenu(false)} />
                        <div className="absolute right-0 mt-1 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 z-40 w-40 text-[10px] text-left font-bold text-slate-700 animate-fade-in">
                          <button
                            type="button"
                            onClick={() => { setDmSortMode('recent'); setShowDmSortMenu(false); }}
                            className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between transition-colors"
                          >
                            <span>Sort by most recent</span>
                            {dmSortMode === 'recent' && <Check size={10} className="text-blue-600" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setDmSortMode('alphabetical'); setShowDmSortMenu(false); }}
                            className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between transition-colors"
                          >
                            <span>Sort alphabetically</span>
                            {dmSortMode === 'alphabetical' && <Check size={10} className="text-blue-600" />}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => handleOpenCreateModal('direct')}
                    className="p-0.5 hover:bg-slate-200 rounded text-slate-500"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {directChannels.length > 0 ? (
                  directChannels.map(c => {
                    const isSelected = activeChannel?.id === c.id && activeShortcut === 'home';
                    return (
                      <button
                        key={c.id}
                        onClick={() => { setActiveShortcut('home'); setActiveChannel(c); }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-between transition-colors ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-100 text-slate-700'}`}
                      >
                        <span className="truncate flex items-center gap-2 flex-1">
                          {renderChannelAvatar(c)}
                          <span className="truncate">{getDMName(c)}</span>
                          {(() => {
                            const otherUser = companyUsers.find(u => u.name === c.name || u.full_name === c.name || getDMName(c) === u.full_name);
                            const statusInfo = otherUser?.id ? statuses[otherUser.id] : null;
                            if (statusInfo?.custom_status_msg) {
                              return (
                                <span className="text-[10px] text-slate-400 font-normal truncate max-w-[80px]" title={statusInfo.custom_status_msg}>
                                  — {statusInfo.custom_status_msg}
                                </span>
                              );
                            }
                            return null;
                          })()}
                          {drafts[c.id] && drafts[c.id].trim().length > 0 && (
                            <span className="text-[7.5px] bg-slate-250 text-slate-500 font-black px-1 py-0.2 rounded scale-90" title="Unsent Draft">DRAFT</span>
                          )}
                        </span>
                        {unreadCounts[c.id] > 0 && <span className="text-[9px] bg-red-500 text-white font-bold px-1.5 py-0.5 rounded-full">{unreadCounts[c.id]}</span>}
                      </button>
                    );
                  })
                ) : (
                  <div className="text-[10px] text-slate-400 px-2 py-1 italic">No chats started</div>
                )}
              </div>
            </div>

            {/* Groups Section */}
            <div style={{ display: 'none' }} className="px-4 pt-2">
              <div className="flex justify-between items-center mb-1 px-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Users size={10} /> Group Chats
                </span>
                <div className="flex items-center gap-1">
                  <div className="relative">
                    <button
                      onClick={() => setShowGroupSortMenu(v => !v)}
                      className="p-0.5 hover:bg-slate-200 rounded text-slate-500 transition-colors"
                      title="Section options"
                    >
                      <MoreVertical size={12} />
                    </button>
                    {showGroupSortMenu && (
                      <>
                        <div className="fixed inset-0 z-30 cursor-default" onClick={() => setShowGroupSortMenu(false)} />
                        <div className="absolute right-0 mt-1 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 z-40 w-40 text-[10px] text-left font-bold text-slate-700 animate-fade-in">
                          <button
                            type="button"
                            onClick={() => { setGroupSortMode('recent'); setShowGroupSortMenu(false); }}
                            className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between transition-colors"
                          >
                            <span>Sort by most recent</span>
                            {groupSortMode === 'recent' && <Check size={10} className="text-blue-600" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setGroupSortMode('alphabetical'); setShowGroupSortMenu(false); }}
                            className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between transition-colors"
                          >
                            <span>Sort alphabetically</span>
                            {groupSortMode === 'alphabetical' && <Check size={10} className="text-blue-600" />}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => handleOpenCreateModal('group')}
                    className="p-0.5 hover:bg-slate-200 rounded text-slate-500"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {groupChannels.length > 0 ? (
                  groupChannels.map(c => {
                    const isSelected = activeChannel?.id === c.id && activeShortcut === 'home';
                    return (
                      <button
                        key={c.id}
                        onClick={() => { setActiveShortcut('home'); setActiveChannel(c); }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-between transition-colors ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-100 text-slate-700'}`}
                      >
                        <span className="truncate flex items-center gap-2 flex-1">
                          {renderChannelAvatar(c)}
                          <span className="truncate">{getDMName(c)}</span>
                          {drafts[c.id] && drafts[c.id].trim().length > 0 && (
                            <span className="text-[7.5px] bg-slate-250 text-slate-500 font-black px-1 py-0.2 rounded scale-90" title="Unsent Draft">DRAFT</span>
                          )}
                        </span>
                        {unreadCounts[c.id] > 0 && <span className="text-[9px] bg-red-500 text-white font-bold px-1.5 py-0.5 rounded-full">{unreadCounts[c.id]}</span>}
                      </button>
                    );
                  })
                ) : (
                  <div className="text-[10px] text-slate-400 px-2 py-1 italic">No groups started</div>
                )}
              </div>
            </div>

            {/* 3. Projects Section */}
            <div className="px-4 pt-2">
              <div className="flex justify-between items-center mb-1 px-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Folder size={10} /> Projects
                </span>
                <div className="flex items-center gap-1">
                  <div className="relative">
                    <button
                      onClick={() => setShowProjectSortMenu(v => !v)}
                      className="p-0.5 hover:bg-slate-200 rounded text-slate-500 transition-colors"
                      title="Section options"
                    >
                      <MoreVertical size={12} />
                    </button>
                    {showProjectSortMenu && (
                      <>
                        <div className="fixed inset-0 z-30 cursor-default" onClick={() => setShowProjectSortMenu(false)} />
                        <div className="absolute right-0 mt-1 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 z-40 w-40 text-[10px] text-left font-bold text-slate-700 animate-fade-in">
                          <button
                            type="button"
                            onClick={() => { setProjectSortMode('recent'); setShowProjectSortMenu(false); }}
                            className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between transition-colors"
                          >
                            <span>Sort by most recent</span>
                            {projectSortMode === 'recent' && <Check size={10} className="text-blue-600" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setProjectSortMode('alphabetical'); setShowProjectSortMenu(false); }}
                            className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between transition-colors"
                          >
                            <span>Sort alphabetically</span>
                            {projectSortMode === 'alphabetical' && <Check size={10} className="text-blue-600" />}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => handleOpenCreateModal('project')}
                    className="p-0.5 hover:bg-slate-200 rounded text-slate-500"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {projectChannels.length > 0 ? (
                  projectChannels.map(c => {
                    const isSelected = activeChannel?.id === c.id && activeShortcut === 'home';
                    return (
                      <button
                        key={c.id}
                        onClick={() => { setActiveShortcut('home'); setActiveChannel(c); }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-between transition-colors ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-100 text-slate-700'}`}
                      >
                        <span className="truncate flex items-center gap-2 flex-1">
                          {renderChannelAvatar(c)}
                          <span className="truncate">{c.name}</span>
                          {drafts[c.id] && drafts[c.id].trim().length > 0 && (
                            <span className="text-[7.5px] bg-slate-250 text-slate-500 font-black px-1 py-0.2 rounded scale-90" title="Unsent Draft">DRAFT</span>
                          )}
                        </span>
                        {unreadCounts[c.id] > 0 && <span className="text-[9px] bg-red-500 text-white font-bold px-1.5 py-0.5 rounded-full">{unreadCounts[c.id]}</span>}
                      </button>
                    );
                  })
                ) : (
                  <div className="text-[10px] text-slate-400 px-2 py-1 italic">No project channels</div>
                )}
              </div>
            </div>

            {/* 4. Channels Section */}
            <div className="px-4 pt-2">
              <div className="flex justify-between items-center mb-1 px-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Hash size={10} /> Channels
                </span>
                <div className="flex items-center gap-1">
                  <div className="relative">
                    <button
                      onClick={() => setShowChannelSortMenu(v => !v)}
                      className="p-0.5 hover:bg-slate-200 rounded text-slate-500 transition-colors"
                      title="Section options"
                    >
                      <MoreVertical size={12} />
                    </button>
                    {showChannelSortMenu && (
                      <>
                        <div className="fixed inset-0 z-30 cursor-default" onClick={() => setShowChannelSortMenu(false)} />
                        <div className="absolute right-0 mt-1 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 z-40 w-40 text-[10px] text-left font-bold text-slate-700 animate-fade-in">
                          <button
                            type="button"
                            onClick={() => { setChannelSortMode('recent'); setShowChannelSortMenu(false); }}
                            className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between transition-colors"
                          >
                            <span>Sort by most recent</span>
                            {channelSortMode === 'recent' && <Check size={10} className="text-blue-600" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setChannelSortMode('alphabetical'); setShowChannelSortMenu(false); }}
                            className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between transition-colors"
                          >
                            <span>Sort alphabetically</span>
                            {channelSortMode === 'alphabetical' && <Check size={10} className="text-blue-600" />}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => handleOpenCreateModal('company')}
                    className="p-0.5 hover:bg-slate-200 rounded text-slate-500"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {regularChannels.length > 0 ? (
                  regularChannels.map(c => {
                    const isSelected = activeChannel?.id === c.id && activeShortcut === 'home';
                    return (
                      <button
                        key={c.id}
                        onClick={() => { setActiveShortcut('home'); setActiveChannel(c); }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-between transition-colors ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-100 text-slate-700'}`}
                      >
                        <span className="truncate flex items-center gap-2 flex-1">
                          {renderChannelAvatar(c)}
                          <span className="truncate">{c.name}</span>
                          {drafts[c.id] && drafts[c.id].trim().length > 0 && (
                            <span className="text-[7.5px] bg-slate-250 text-slate-500 font-black px-1 py-0.2 rounded scale-90" title="Unsent Draft">DRAFT</span>
                          )}
                        </span>
                        {unreadCounts[c.id] > 0 && <span className="text-[9px] bg-red-500 text-white font-bold px-1.5 py-0.5 rounded-full">{unreadCounts[c.id]}</span>}
                      </button>
                    );
                  })
                ) : (
                  <div className="text-[10px] text-slate-400 px-2 py-1 italic">No general channels</div>
                )}
              </div>
            </div>

          </div>
        </div>

        {activeShortcut === 'home' && (
          <div
            onMouseDown={startResizeCol1}
            className="w-1 cursor-col-resize hover:bg-blue-500 bg-transparent transition-colors z-20 flex-shrink-0"
            style={{ marginRight: '-2.5px', marginLeft: '-2.5px' }}
          />
        )}

        {/* Column 2: Middle List (Home Threads / Mentions / Starred) */}
        <div
          ref={col2Ref}
          style={{ width: `${col2Width}px`, borderColor: '#e0e0e0', display: activeShortcut !== 'home' ? 'flex' : 'none' }}
          className="border-r flex flex-col bg-white flex-shrink-0"
        >

          {/* Search Capsule Bar */}
          <div className="p-3 border-b flex flex-col gap-2" style={{ borderColor: '#e0e0e0' }}>
            <div className="relative flex items-center bg-slate-100 rounded-full px-3 py-1.5">
              <Search className="text-slate-400 mr-2" size={14} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search in chat..."
                className="w-full bg-transparent border-none outline-none text-xs text-slate-800 placeholder-slate-400"
              />
              {searchQuery && (
                <X
                  className="text-slate-400 cursor-pointer hover:text-slate-600"
                  size={14}
                  onClick={() => setSearchQuery('')}
                />
              )}
            </div>

            {/* Quick filters */}
            <div className="flex items-center justify-between px-1 text-slate-500 text-xs">
              <label className="flex items-center gap-2 font-bold cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={unreadOnly}
                  onChange={e => setUnreadOnly(e.target.checked)}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                Unread
              </label>
              <div className="flex items-center gap-1.5 font-bold cursor-pointer">
                <span>Thread</span>
                <ChevronDown size={12} />
              </div>
            </div>
          </div>

          {/* Thread Lists based on shortcut */}
          <div className="flex-1 overflow-y-auto">
            {activeShortcut === 'home' && (
              <div className="divide-y" style={{ borderColor: '#f0f0f0' }}>
                {filteredHomeChannels.length === 0 ? (
                  <div className="p-8 text-center text-xs text-slate-400">No chats match filters</div>
                ) : (
                  filteredHomeChannels.map(c => {
                    const isSelected = activeChannel?.id === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => setActiveChannel(c)}
                        className={`w-full text-left px-4 py-3 border-b flex items-start gap-3 transition-colors ${isSelected ? 'bg-blue-50/70 border-l-4 border-blue-600 pl-3' : 'hover:bg-slate-50'}`}
                        style={{ borderColor: '#f0f0f0' }}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {renderChannelAvatar(c)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-baseline mb-0.5">
                            <span className="font-bold text-xs text-slate-800 truncate">
                              {c.type === 'direct' ? getDMName(c) : c.name}
                            </span>
                            <span className="text-[10px] text-slate-400 font-medium">
                              {getThreadDate(c.last_message_time)}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {c.last_message_sender ? (
                              <span>
                                <span className="font-bold">{c.last_message_sender}: </span>
                                {c.last_message_text}
                              </span>
                            ) : (
                              <span className="italic">No messages yet</span>
                            )}
                          </div>
                        </div>
                        {unreadCounts[c.id] > 0 && (
                          <div className="bg-red-500 text-white font-bold rounded-full px-1.5 py-0.5 text-[9px] min-w-[16px] text-center self-center">
                            {unreadCounts[c.id]}
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {activeShortcut === 'starred' && (
              <div className="divide-y" style={{ borderColor: '#f0f0f0' }}>
                <div className="px-4 py-2 bg-slate-50 font-bold text-[10px] text-slate-500 uppercase tracking-wider">Starred Messages ({starredMessages.length})</div>
                {starredMessages.length === 0 ? (
                  <div className="p-8 text-center text-xs text-slate-400">No starred messages yet</div>
                ) : (
                  starredMessages.map(sm => (
                    <button
                      key={sm.id}
                      onClick={() => {
                        const c = channels.find(ch => ch.id === sm.channel_id);
                        if (c) setActiveChannel(c);
                      }}
                      className="w-full text-left p-3 hover:bg-slate-50 flex flex-col gap-1 transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-xs text-blue-600 flex items-center gap-1">★ {sm.sender_name}</span>
                        <span className="text-[9px] text-slate-400">{getThreadDate(sm.created_at)}</span>
                      </div>
                      <div className="text-xs text-slate-700 truncate">{sm.message_text}</div>
                      <div className="text-[10px] text-slate-400 mt-1">in #{sm.channel_name}</div>
                    </button>
                  ))
                )}
              </div>
            )}

            {activeShortcut === 'mentions' && (
              <div className="divide-y" style={{ borderColor: '#f0f0f0' }}>
                <div className="px-4 py-2 bg-slate-50 font-bold text-[10px] text-slate-500 uppercase tracking-wider">Mentions ({mentionMessages.length})</div>
                {mentionMessages.length === 0 ? (
                  <div className="p-8 text-center text-xs text-slate-400">No mentions of you found</div>
                ) : (
                  mentionMessages.map(mm => (
                    <button
                      key={mm.id}
                      onClick={() => {
                        const c = channels.find(ch => ch.id === (mm.channelId || mm.channel_id));
                        if (c) setActiveChannel(c);
                      }}
                      className="w-full text-left p-3 hover:bg-slate-50 flex flex-col gap-1 transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-xs text-indigo-600 flex items-center gap-1">@ {mm.sender_name}</span>
                        <span className="text-[9px] text-slate-400">{getThreadDate(mm.created_at)}</span>
                      </div>
                      <div className="text-xs text-slate-700 truncate">{mm.message_text}</div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

        </div>

        {activeShortcut !== 'home' && (
          <div
            onMouseDown={startResizeCol2}
            className="w-1 cursor-col-resize hover:bg-blue-500 bg-transparent transition-colors z-20 flex-shrink-0"
            style={{ marginRight: '-2.5px', marginLeft: '-2.5px' }}
          />
        )}

        {/* Column 3: Main Chat Pane */}
        <div className="flex-1 flex flex-col bg-white overflow-hidden">
          {activeChannel ? (
            <>
              {/* Active Channel Header */}
              <div className="h-14 bg-white border-b px-6 flex justify-between items-center shadow-sm flex-shrink-0" style={{ borderColor: '#e0e0e0' }}>
                <div className="flex items-center gap-3">
                  <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    {activeChannel.type === 'announcement' ? '📢' : activeChannel.type === 'project' ? '📁' : activeChannel.type === 'direct' ? '💬' : '#'}
                    {activeChannel.type === 'direct' ? getDMName(activeChannel) : activeChannel.name}
                    {activeChannel.type !== 'direct' && activeChannel.is_member !== false && has('chat.edit_channel') && (
                      <button
                        onClick={() => {
                          setEditChanName(activeChannel.name);
                          setEditChanAvatarUrl(activeChannel.avatar_url || '');
                          setShowEditChanModal(true);
                        }}
                        className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors"
                        title="Edit settings"
                      >
                        <Edit2 size={12} />
                      </button>
                    )}
                  </h2>
                  <div style={{ display: 'none' }} className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-[10px] font-bold rounded">External</div>
                  {/* {activeChannel.type !== 'direct' && has('chat.manage_members') && (
                    <button
                      onClick={() => setShowMembersModal(true)}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 font-bold transition-all shadow-sm"
                    >
                      <Users size={12} />
                      Members
                    </button>
                  )} */}
                </div>

                <div className="flex items-center gap-3 text-slate-500 text-xs">

                  {/* Active user status selector */}
                  <div style={{ display: 'none' }} className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded-full cursor-pointer font-bold">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span>Active</span>
                    <ChevronDown size={12} />
                  </div>

                  {chatSettings.conversationSummary && (
                    <button
                      style={{ display: 'none' }}
                      onClick={handleSummarize}
                      disabled={summarizing}
                      className="px-3 py-1 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200 rounded flex items-center gap-1 font-semibold transition-all disabled:opacity-50"
                      data-testid="summarize-btn"
                    >
                      <Sparkles size={14} className={summarizing ? 'animate-spin' : ''} />
                      {summarizing ? 'Summarizing...' : 'AI Summarize'}
                    </button>
                  )}

                  <button
                    style={{ display: 'none' }}
                    onClick={() => {
                      if (inHuddle && huddleActiveChannel?.id === activeChannel.id) {
                        leaveHuddle();
                      } else {
                        joinHuddle(activeChannel);
                        if (socket) {
                          const targetUserId = activeChannel.type === 'dm' ? activeChannel.members?.find(m => m !== user?.id) : null;
                          socket.emit('huddle:ring', {
                            channelId: activeChannel.id,
                            callerName: user?.full_name || user?.email,
                            callerAvatar: user?.avatar_url || user?.avatar,
                            targetUserId
                          });
                        }
                      }
                    }}
                    className={`p-1 rounded hover:bg-slate-100 transition-colors flex items-center justify-center ${inHuddle && huddleActiveChannel?.id === activeChannel.id ? 'text-red-500 bg-red-50 hover:bg-red-100 animate-pulse' : 'text-slate-500 hover:text-slate-800'}`}
                    title={inHuddle && huddleActiveChannel?.id === activeChannel.id ? "Leave Audio Huddle" : "Join Audio Huddle"}
                    data-testid="join-huddle-btn"
                  >
                    <Phone size={16} />
                  </button>

                  <button
                    style={{ display: 'none' }}
                    onClick={() => setShowChannelDetails(v => !v)}
                    className={`p-1 rounded hover:bg-slate-100 transition-colors ${showChannelDetails ? 'text-blue-600 bg-blue-50' : 'text-slate-500 hover:text-slate-800'}`}
                    title="Channel Details"
                    data-testid="channel-details-toggle"
                  >
                    <Info size={16} />
                  </button>
                  {activeChannel.type !== 'direct' && activeChannel.is_member !== false && has('chat.manage_members') && (
                    <button
                      title='Manage Members'
                      onClick={() => setShowMembersModal(true)}
                      className="flex items-center gap-1.5 px-2.5 py-2.5 cursor-pointer hover:text-slate-800 font-bold transition-all"
                    >
                      <Users size={12} />
                      {/* Member */}
                    </button>
                  )}
                  <div className="h-4 w-[1px] bg-slate-200" />
                  <Settings title='Chat Settings' size={16} className="cursor-pointer hover:text-slate-800" onClick={() => setShowSettingsModal(true)} />
                  <Grid style={{ display: 'none' }} size={16} className="cursor-pointer hover:text-slate-800" />
                </div>
              </div>

              {/* Pinned Messages Slide-Down Panel */}
              {pinnedMessages.length > 0 && (
                <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center justify-between text-xs text-amber-800 flex-shrink-0" data-testid="pinned-header">
                  <span className="font-semibold flex items-center gap-1.5">📌 Latest Pinned: {pinnedMessages[pinnedMessages.length - 1].message_text}</span>
                  <button
                    onClick={() => setShowPinnedList(v => !v)}
                    className="underline font-bold text-amber-900 hover:text-amber-950"
                    data-testid="toggle-pinned-list"
                  >
                    {showPinnedList ? 'Hide All' : `Show All (${pinnedMessages.length})`}
                  </button>
                </div>
              )}

              {showPinnedList && pinnedMessages.length > 0 && (
                <div className="bg-white border-b p-4 max-h-40 overflow-y-auto space-y-2 text-xs shadow-inner flex-shrink-0" data-testid="pinned-list-container">
                  <div className="font-bold text-slate-500 mb-1">Pinned Messages</div>
                  {pinnedMessages.map(pm => (
                    <div key={pm.id} className="flex justify-between items-center bg-slate-50 p-2 rounded border">
                      <div>
                        <span className="font-bold text-slate-700 mr-2">{pm.sender_name}:</span>
                        <span>{pm.message_text}</span>
                      </div>
                      {(user.role === 'leader' || user.role === 'manager') && (
                        <button
                          onClick={() => handleTogglePin(pm.id)}
                          className="text-red-500 hover:underline text-[10px] font-bold"
                          data-testid={`unpin-list-${pm.id}`}
                        >
                          Unpin
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Scrollable Messages Pane */}
              <div
                ref={messagesContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50 relative"
              >

                {/* Google Chat Style Chat Details Banner Card */}
                <div className="bg-white border rounded-xl p-6 shadow-sm max-w-xl mx-auto text-center space-y-3" style={{ borderColor: '#e0e0e0' }}>
                  <span className="font-bold text-slate-800 text-sm block">Chat for {activeChannel.type === 'direct' ? getDMName(activeChannel) : activeChannel.name}</span>
                  <div className="flex justify-center items-center gap-2 text-xs font-semibold text-slate-500">
                    <span className="flex items-center gap-1">⌚ HISTORY IS ON</span>
                    <span>•</span>
                    <span>Messages sent with history on are saved</span>
                  </div>
                  <p className="text-slate-400 text-[11px] leading-relaxed">
                    This chat is only visible to team members of this channel and will persist logs in the secure BexConnect environment.
                  </p>
                </div>

                {messages.map((msg, idx) => {
                  const showUnreadDivider = channelUnreadCount > 0 && idx === messages.length - channelUnreadCount;
                  const isSystem = msg.is_system === 1 || msg.is_system;

                  if (isSystem) {
                    return (
                      <React.Fragment key={msg.id}>
                        {showUnreadDivider && (
                          <div className="flex items-center my-6 animate-fade-in">
                            <div className="flex-1 h-[1px] bg-red-200" />
                            <span className="px-3 text-[10px] text-red-500 font-bold uppercase tracking-wider">New Messages</span>
                            <div className="flex-1 h-[1px] bg-red-200" />
                          </div>
                        )}
                        <div className="flex justify-center my-3 text-center w-full">
                          <div className="bg-slate-50 border border-slate-150 rounded-full px-4 py-1.5 text-[11px] text-slate-500 font-semibold max-w-lg shadow-sm">
                            {msg.message_text}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  }

                  const isMe = msg.senderId === user.id || msg.sender_id === user.id;
                  const canModerate = user.role === 'leader' || user.role === 'manager';
                  const senderInitials = msg.sender_name ? msg.sender_name.slice(0, 2).toUpperCase() : 'U';

                  return (
                    <React.Fragment key={msg.id}>
                      {showUnreadDivider && (
                        <div className="flex items-center my-6 animate-fade-in">
                          <div className="flex-1 h-[1px] bg-red-200" />
                          <span className="px-3 text-[10px] text-red-500 font-bold uppercase tracking-wider">New Messages</span>
                          <div className="flex-1 h-[1px] bg-red-200" />
                        </div>
                      )}
                      <div className="flex gap-4 group relative" data-testid={`message-item-${msg.id}`}>

                        {/* User Avatar Circle */}
                        <div className="w-9 h-9 rounded-full bg-blue-600 text-white font-bold text-xs flex items-center justify-center shadow-sm flex-shrink-0">
                          {senderInitials}
                        </div>

                        <div className="flex-1 min-w-0">

                          {/* Message Metadata Header */}
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-bold text-xs text-slate-800">{msg.sender_name}</span>
                            <span className="text-[10px] text-slate-400 font-semibold">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>

                            {/* Read Receipts */}
                            {isMe && (
                              <span className="ml-1 self-center">
                                {msg.status === 'read' ? (
                                  <CheckCheck size={13} className="text-[#34B7F1] inline" title="Read" />
                                ) : msg.status === 'delivered' ? (
                                  <CheckCheck size={13} className="text-slate-400 inline" title="Delivered" />
                                ) : (
                                  <Check size={13} className="text-slate-400 inline" title="Sent" />
                                )}
                              </span>
                            )}

                            {/* Starred Status */}
                            {starredMessages.some(sm => sm.id === msg.id) && (
                              <Star size={10} className="fill-yellow-400 text-yellow-500" />
                            )}

                            {/* Floating hover controls */}
                            <div className="absolute right-2 top-0 opacity-0 group-hover:opacity-100 bg-white border rounded-lg shadow-md px-1 py-0.5 flex gap-1 items-center z-10 transition-opacity" style={{ borderColor: '#e0e0e0' }}>
                              {/* Quick Emoji Reactions */}
                              {chatSettings.allowReactions && (
                                <div className="flex gap-0.5 border-r pr-1 mr-1 border-slate-150">
                                  {['👍', '❤️', '😂', '🔥'].map(emoji => (
                                    <button
                                      key={emoji}
                                      onClick={() => handleToggleReaction(msg.id, emoji)}
                                      className="hover:scale-125 px-1 py-0.5 text-xs transition-transform"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <button
                                onClick={() => handleToggleStarMessage(msg)}
                                className={`p-0.5 rounded hover:bg-slate-200 ${starredMessages.some(sm => sm.id === msg.id) ? 'text-yellow-500' : 'text-slate-400'}`}
                                title={starredMessages.some(sm => sm.id === msg.id) ? 'Unstar message' : 'Star message'}
                              >
                                <Star size={10} className={starredMessages.some(sm => sm.id === msg.id) ? 'fill-yellow-500' : ''} />
                              </button>
                              <button
                                onClick={() => setActiveThreadMessage(msg)}
                                className="p-0.5 rounded hover:bg-slate-200 text-slate-400"
                                title="Reply in thread"
                                data-testid={`reply-thread-${msg.id}`}
                              >
                                <MessageSquare size={10} />
                              </button>
                              {canModerate && (
                                <button
                                  onClick={() => handleTogglePin(msg.id)}
                                  className={`p-0.5 rounded hover:bg-slate-200 ${msg.is_pinned ? 'text-amber-500' : 'text-slate-400'}`}
                                  title={msg.is_pinned ? 'Unpin message' : 'Pin message'}
                                >
                                  <Pin size={10} />
                                </button>
                              )}

                              {/* More Options Dropdown Trigger */}
                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMenuMessageId(activeMenuMessageId === msg.id ? null : msg.id);
                                  }}
                                  className="p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                                  title="More options"
                                >
                                  <MoreVertical size={10} />
                                </button>
                                {activeMenuMessageId === msg.id && (
                                  <>
                                    <div className="fixed inset-0 z-30 cursor-default" onClick={() => setActiveMenuMessageId(null)} />
                                    <div className="absolute right-0 mt-1 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 z-40 w-36 text-[10px] text-left font-bold text-slate-700 animate-fade-in">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          navigator.clipboard.writeText(msg.message_text || '');
                                          setActiveMenuMessageId(null);
                                          alert('Copied to clipboard!');
                                        }}
                                        className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-slate-600 transition-colors"
                                      >
                                        <span>Copy Text</span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          handleToggleStarMessage(msg);
                                          setActiveMenuMessageId(null);
                                        }}
                                        className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-slate-600 transition-colors"
                                      >
                                        <span>{starredMessages.some(sm => sm.id === msg.id) ? 'Unstar' : 'Star'}</span>
                                      </button>
                                      {canModerate && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            handleTogglePin(msg.id);
                                            setActiveMenuMessageId(null);
                                          }}
                                          className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-slate-600 transition-colors"
                                        >
                                          <span>{msg.is_pinned ? 'Unpin' : 'Pin'}</span>
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          handleShowMessageInfo(msg);
                                          setActiveMenuMessageId(null);
                                        }}
                                        className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-slate-600 transition-colors border-t border-slate-50"
                                      >
                                        <span>Message Info</span>
                                      </button>
                                      {isMe && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditingMessageId(msg.id);
                                            setEditingText(msg.message_text || '');
                                            setActiveMenuMessageId(null);
                                          }}
                                          className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-blue-600 transition-colors border-t border-slate-50"
                                        >
                                          <span>Edit Message</span>
                                        </button>
                                      )}
                                      {(isMe || canModerate) && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            handleDeleteMessage(msg.id);
                                            setActiveMenuMessageId(null);
                                          }}
                                          className="w-full px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-red-600 transition-colors border-t border-slate-50"
                                        >
                                          <span>Delete Message</span>
                                        </button>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                          </div>

                          {/* Message Bubble text content */}
                          <div className="text-xs text-slate-700 leading-relaxed font-sans font-medium whitespace-pre-wrap">
                            {editingMessageId === msg.id ? (
                              <div className="flex flex-col gap-1.5 mt-1 bg-slate-50 p-2.5 rounded-xl border border-slate-200 max-w-md">
                                <textarea
                                  value={editingText}
                                  onChange={(e) => setEditingText(e.target.value)}
                                  rows={2}
                                  className="w-full p-2 border rounded-lg text-xs outline-none bg-white focus:border-blue-500 resize-none font-medium text-slate-800"
                                />
                                <div className="flex justify-end gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => setEditingMessageId(null)}
                                    className="px-2.5 py-1 text-[10px] bg-slate-200 hover:bg-slate-300 font-bold text-slate-700 rounded-lg transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleUpdateMessage(msg.id, editingText)}
                                    className="px-2.5 py-1 text-[10px] bg-blue-600 hover:bg-blue-700 font-bold text-white rounded-lg transition-colors"
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              msg.message_text && <p>{renderMessageText(msg.message_text)}</p>
                            )}

                            {/* Attachments preview */}
                            {msg.attachment_url && (
                              <div className="mt-2 p-2 rounded-xl flex items-center gap-3 border bg-white border-slate-200 text-slate-800 max-w-sm">
                                {msg.attachment_url.match(/\.(jpeg|jpg|gif|png|webp)/i) ? (
                                  <div className="flex flex-col gap-1 w-full">
                                    <img
                                      src={msg.attachment_url}
                                      alt={msg.attachment_name}
                                      className="rounded-lg object-cover cursor-pointer hover:opacity-90 max-h-40 w-full"
                                      onClick={() => window.open(msg.attachment_url, '_blank')}
                                    />
                                    <a
                                      href={msg.attachment_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[10px] flex items-center gap-1 underline text-blue-600 font-bold"
                                    >
                                      <Download size={10} /> Download Image
                                    </a>
                                  </div>
                                ) : (
                                  <>
                                    <FileText size={18} className="text-blue-600" />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-bold text-[10px] truncate text-slate-800">{msg.attachment_name}</div>
                                      <div className="text-[9px] text-slate-400">{formatSize(msg.attachment_size)}</div>
                                    </div>
                                    <a
                                      href={msg.attachment_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1 hover:bg-slate-100 rounded transition-colors text-slate-600"
                                      title="Download File"
                                    >
                                      <Download size={12} />
                                    </a>
                                  </>
                                )}
                              </div>
                            )}

                            {/* Selected reactions list */}
                            {msg.reactions && msg.reactions.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {Object.entries(
                                  msg.reactions.reduce((acc, r) => {
                                    acc[r.emoji] = acc[r.emoji] || [];
                                    acc[r.emoji].push(r);
                                    return acc;
                                  }, {})
                                ).map(([emoji, reacts]) => {
                                  const hasReacted = reacts.some(r => r.user_id === user.id);
                                  return (
                                    <button
                                      key={emoji}
                                      onClick={() => handleToggleReaction(msg.id, emoji)}
                                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors ${hasReacted
                                        ? 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100'
                                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                        }`}
                                      title={reacts.map(r => r.user_name || 'Someone').join(', ')}
                                      data-testid={`msg-react-${msg.id}-${emoji}`}
                                    >
                                      <span>{emoji}</span>
                                      <span>{reacts.length}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            {/* Thread replies summary */}
                            {msg.reply_count > 0 && (
                              <button
                                onClick={() => setActiveThreadMessage(msg)}
                                className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-blue-600 hover:underline bg-blue-50/50 hover:bg-blue-100 px-2 py-1 rounded-lg w-fit border border-blue-100/50 transition-colors"
                                data-testid={`view-thread-${msg.id}`}
                              >
                                <MessageSquare size={10} />
                                {msg.reply_count === 1 ? '1 reply' : `${msg.reply_count} replies`}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
                {showNewMessageBanner && (
                  <button
                    onClick={() => scrollToBottom(true)}
                    className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-4 py-2 rounded-full shadow-lg text-xs flex items-center gap-1.5 transition-all animate-bounce z-10"
                  >
                    <ChevronDown size={14} />
                    New Messages
                  </button>
                )}
              </div>

              {/* Redesigned Bottom Message Composer Box */}
              <div className="p-4 bg-white border-t flex flex-col gap-2 flex-shrink-0" style={{ borderColor: '#e0e0e0' }}>
                {/* Typing Indicators */}
                {(() => {
                  const activeChannelTyping = typingUsers[activeChannel?.id] || {};
                  const typingNames = Object.values(activeChannelTyping).filter(name => name !== user?.full_name);
                  if (typingNames.length === 0) return null;
                  return (
                    <div className="px-2 py-0.5 text-slate-500 text-[10px] italic flex items-center gap-1.5 animate-pulse font-bold">
                      <span className="flex gap-1 items-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:0.2s]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:0.4s]" />
                      </span>
                      <span>{typingNames.join(', ')} {typingNames.length === 1 ? 'is' : 'are'} typing...</span>
                    </div>
                  );
                })()}

                {/* AI Smart Replies Chips */}
                {chatSettings.smartReply && smartReplies.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap mb-1 animate-fade-in" data-testid="smart-replies">
                    {smartReplies.map((reply, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          setInputText(reply);
                          if (activeChannel) {
                            setDraft(activeChannel.id, reply);
                          }
                        }}
                        className="px-3 py-1 bg-blue-50 border border-blue-150 text-blue-700 rounded-full text-xs font-semibold hover:bg-blue-100 transition-colors shadow-sm cursor-pointer flex items-center gap-1 font-sans"
                      >
                        ✨ {reply}
                      </button>
                    ))}
                  </div>
                )}

                {/* Autocomplete suggestions */}
                {showTaskSuggest && filteredTasks.length > 0 && (
                  <div className="absolute bottom-28 left-6 right-6 bg-white border rounded-xl shadow-lg max-h-40 overflow-y-auto z-50 p-2 text-xs" style={{ borderColor: '#e0e0e0' }}>
                    <div className="font-bold text-slate-400 mb-1 px-1">Select a task to reference:</div>
                    {filteredTasks.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleSelectTaskReference(t)}
                        className="w-full text-left p-1.5 hover:bg-slate-50 rounded flex items-center justify-between border-b"
                      >
                        <span className="font-semibold text-slate-800 truncate">{t.title}</span>
                        <span className="text-[9px] bg-slate-200 px-1 py-0.2 rounded uppercase">{t.task_status || 'new'}</span>
                      </button>
                    ))}
                  </div>
                )}

                {showUserSuggest && filteredUsers.length > 0 && (
                  <div className="absolute bottom-28 left-6 right-6 bg-white border rounded-xl shadow-lg max-h-40 overflow-y-auto z-50 p-2 text-xs" style={{ borderColor: '#e0e0e0' }}>
                    <div className="font-bold text-slate-400 mb-1 px-1">Select member to mention:</div>
                    {filteredUsers.map(u => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => handleSelectUserReference(u)}
                        className="w-full text-left p-1.5 hover:bg-slate-50 rounded flex items-center gap-2 border-b"
                      >
                        <Avatar user={u} size={20} />
                        <span className="font-semibold text-slate-800">{u.name}</span>
                        <span className="text-[10px] text-slate-400">({u.role})</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Custom Composer Inner Container (Google Chat Style) */}
                <div className="border rounded-2xl p-3 bg-white flex flex-col gap-2 shadow-sm" style={{ borderColor: '#ccd0d5' }}>

                  {/* File Attachment preview */}
                  {attachment && (
                    <div className="p-2 bg-slate-50 border rounded-lg flex items-center justify-between text-xs mb-1">
                      <div className="flex items-center gap-2 truncate">
                        <Paperclip size={12} className="text-blue-600" />
                        <span className="font-semibold truncate">{attachment.name}</span>
                        <span className="text-slate-500">({formatSize(attachment.size)})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAttachment(null)}
                        className="p-1 rounded hover:bg-slate-200 text-slate-600"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  )}

                  {/* Form Input Row */}
                  {activeChannel && activeChannel.is_member === false ? (
                    <div className="p-4 bg-slate-50 border-t text-center text-xs text-slate-500 font-semibold flex items-center justify-center gap-2">
                      <span className="text-base">👁️</span> You are no longer a member of this channel. You can only view the history.
                    </div>
                  ) : (
                    <form onSubmit={handleSendMessage} className="p-3 bg-white border-t">
                    <div className="flex items-center gap-2">
                      {/* The main rounded pill input box */}
                      <div className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2 flex items-center justify-between shadow-sm focus-within:border-blue-400 focus-within:bg-white transition-all relative">
                        {/* Smart Compose Inline Ghost Helper */}
                        {composeSuggestion && (
                          <div className="absolute left-4 -top-8 px-2 py-1 bg-blue-600 text-white text-[10px] rounded-lg shadow-md flex items-center gap-1.5 animate-slide-down font-bold z-10">
                            <span>Press <kbd className="bg-white/20 px-1 rounded">Tab</kbd> to insert: <em>"{composeSuggestion}"</em></span>
                            <button
                              type="button"
                              onClick={() => {
                                setInputText(prev => prev + composeSuggestion);
                                setComposeSuggestion('');
                              }}
                              className="bg-white text-blue-600 px-1 py-0.2 rounded hover:bg-slate-100 transition-colors"
                            >
                              Accept
                            </button>
                          </div>
                        )}

                        <textarea
                          ref={chatInputRef}
                          value={inputText}
                          onChange={handleInputChange}
                          rows={1}
                          placeholder={activeChannel.type === 'announcement' && !has('chat.announcements_create') ? "Only Leaders and Managers can post here." : "History is on"}
                          disabled={activeChannel.type === 'announcement' && !has('chat.announcements_create')}
                          className="flex-1 resize-none bg-transparent outline-none text-xs text-slate-800 placeholder-slate-400 font-medium py-1 min-h-[24px]"
                          onKeyDown={(e) => {
                            if (e.key === 'Tab' && composeSuggestion) {
                              e.preventDefault();
                              setInputText(prev => prev + composeSuggestion);
                              setComposeSuggestion('');
                              return;
                            }
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                        />

                        {/* Actions inside the pill */}
                        <div className="flex items-center gap-2 text-slate-400 flex-shrink-0 ml-2 border-l pl-2">
                          {/* Markdown formatting buttons */}
                          <button
                            type="button"
                            onClick={() => insertMarkdown('bold')}
                            className="p-1 hover:text-slate-600 rounded hover:bg-slate-200 transition-colors text-xs font-bold"
                            title="Bold"
                          >
                            B
                          </button>
                          <button
                            type="button"
                            onClick={() => insertMarkdown('italic')}
                            className="p-1 hover:text-slate-600 rounded hover:bg-slate-200 transition-colors text-xs italic"
                            title="Italic"
                          >
                            I
                          </button>
                          <button
                            type="button"
                            onClick={() => insertMarkdown('code')}
                            className="p-1 hover:text-slate-600 rounded hover:bg-slate-200 transition-colors text-[10px] font-mono"
                            title="Code block"
                          >
                            &lt;/&gt;
                          </button>
                          <button
                            type="button"
                            onClick={() => insertMarkdown('strike')}
                            className="p-1 hover:text-slate-600 rounded hover:bg-slate-200 transition-colors text-xs line-through"
                            title="Strikethrough"
                          >
                            S
                          </button>

                          {/* 2. Emoji Picker Button */}
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => {
                                setShowEmojiPicker(v => !v);
                                setShowGifPicker(false);
                              }}
                              className="p-1 hover:text-slate-600 rounded transition-colors text-xs leading-none"
                              title="Emoji picker"
                            >
                              😊
                            </button>
                            {/* Emoji Selector Popup Popover */}
                            {showEmojiPicker && (
                              <>
                                <div className="fixed inset-0 z-30 cursor-default" onClick={() => setShowEmojiPicker(false)} />
                                <div className="absolute right-0 bottom-10 bg-white rounded-2xl shadow-xl border border-slate-100 p-3 z-40 w-52 grid grid-cols-6 gap-1.5 max-h-48 overflow-y-auto animate-fade-in">
                                  {['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👹', '👺', '🤡', '👻', '💀', '☠️', '👽', '👾', '🤖', '🎃', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾'].map(emoji => (
                                    <button
                                      key={emoji}
                                      type="button"
                                      onClick={() => {
                                        setInputText(prev => prev + emoji);
                                        setShowEmojiPicker(false);
                                      }}
                                      className="text-lg hover:bg-slate-50 rounded transition-colors flex items-center justify-center p-1"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>

                          {/* 3. GIF / Sticker Button */}
                          {has('chat.send_gifs') && (
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowGifPicker(v => !v);
                                  setShowEmojiPicker(false);
                                  if (!gifs.length) loadTrendingGifs();
                                }}
                                className="p-1 hover:text-slate-600 rounded transition-colors flex items-center"
                                title="GIF / Sticker"
                              >
                                <Image size={14} />
                              </button>
                              {/* GIF Selector Popup Popover */}
                              {showGifPicker && (
                                <>
                                  <div className="fixed inset-0 z-30 cursor-default" onClick={() => setShowGifPicker(false)} />
                                  <div className="absolute right-0 bottom-10 bg-white rounded-2xl shadow-xl border border-slate-100 p-3 z-40 w-64 flex flex-col gap-2 animate-fade-in max-h-72">
                                    <div className="flex gap-1.5 items-center flex-shrink-0">
                                      <input
                                        type="text"
                                        placeholder="Search GIFs on Giphy..."
                                        value={gifSearchQuery}
                                        onChange={e => setGifSearchQuery(e.target.value)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            searchGifs(gifSearchQuery);
                                          }
                                        }}
                                        className="flex-1 px-2.5 py-1 border rounded-lg text-[10px] outline-none focus:border-blue-500"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => searchGifs(gifSearchQuery)}
                                        className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[10px] font-bold"
                                      >
                                        Search
                                      </button>
                                    </div>

                                    <div className="grid grid-cols-2 gap-1.5 overflow-y-auto flex-1 max-h-56 min-h-[160px] relative">
                                      {loadingGifs ? (
                                        <div className="col-span-2 text-center text-xs text-slate-500 font-semibold py-8">Searching Giphy...</div>
                                      ) : gifs.length > 0 ? (
                                        gifs.map(g => (
                                          <div
                                            key={g.id}
                                            onClick={() => handleSendGif(g.images.fixed_height.url, g.title)}
                                            className="cursor-pointer rounded-lg overflow-hidden border bg-slate-50 hover:opacity-90 relative h-20"
                                          >
                                            <img
                                              src={g.images.fixed_height_small?.url || g.images.fixed_height?.url}
                                              alt={g.title}
                                              className="w-full h-full object-cover"
                                            />
                                          </div>
                                        ))
                                      ) : (
                                        <div className="col-span-2 text-center text-xs text-slate-500 font-semibold py-8">No GIFs found</div>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          {/* 4. File Upload (Up arrow style/attachment) */}
                          {has('chat.share_file') && (
                            <>
                              <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="p-1 hover:text-slate-600 rounded transition-colors"
                                title="Upload file"
                              >
                                {uploading ? (
                                  <div className="w-3.5 h-3.5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Paperclip size={14} />
                                )}
                              </button>
                              <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                className="hidden"
                              />
                            </>
                          )}

                          {/* 5. Google Meet Icon */}
                          <button
                            type="button"
                            onClick={() => {
                              setInputText(prev => prev + ' Join Google Meet Call: https://meet.google.com/abc-defg-hij ');
                            }}
                            className="p-1 hover:text-slate-600 rounded transition-colors"
                            title="Add Google Meet link"
                          >
                            <Video size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Send button next to pill */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          type="submit"
                          disabled={(activeChannel.type === 'announcement' && !has('chat.announcements_create')) || (!inputText.trim() && !attachment)}
                          className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-40 transition-colors shadow-sm flex items-center justify-center"
                          title="Send message"
                        >
                          <Send size={14} />
                        </button>
                      </div>
                    </div>
                  </form>
                  )}

                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
              <MessageSquare size={48} className="stroke-1 mb-2" />
              <span>Select a channel or conversation to start chatting.</span>
            </div>
          )}
        </div>

        {activeThreadMessage && (
          <ThreadPanel
            message={activeThreadMessage}
            onClose={() => setActiveThreadMessage(null)}
            currentUser={user}
            activeChannel={activeChannel}
          />
        )}

        {showChannelDetails && (
          <ChannelDetailsPanel
            channel={activeChannel}
            members={channelMembers}
            pinnedMessages={pinnedMessages}
            messages={messages}
            onClose={() => setShowChannelDetails(false)}
            onUnpin={handleTogglePin}
          />
        )}

        {showTaskPanel && (
          <TaskDetail taskId={activeTaskId} onClose={clearActiveTask} currentUser={user} />
        )}

        {/* Column 4: Far-Right Google Suite Mini-bar Toolbar */}
        <div className="w-12 border-l flex flex-col items-center py-6 gap-6 bg-slate-50 flex-shrink-0" style={{ borderColor: '#e0e0e0', display: 'none' }}>
          <button className="p-1 hover:bg-slate-200 rounded transition-colors text-blue-600" title="Google Calendar">
            <Calendar size={18} />
          </button>
          <button className="p-1 hover:bg-slate-200 rounded transition-colors text-yellow-500 font-bold text-xs" title="Google Keep">
            📁
          </button>
          <button className="p-1 hover:bg-slate-200 rounded transition-colors text-blue-500" title="Google Tasks">
            <Check size={18} />
          </button>
          <button className="p-1 hover:bg-slate-200 rounded transition-colors text-teal-600" title="Google Contacts">
            <Users size={18} />
          </button>
          <div className="h-[1px] w-6 bg-slate-200" />
          <button className="p-1 hover:bg-slate-200 rounded transition-colors text-slate-500" title="Add-ons">
            <Plus size={18} />
          </button>
        </div>

      </div>

      {/* Creation Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden border">
            <div className="flex justify-between items-center p-4 border-b bg-slate-50">
              <h3 className="font-bold text-slate-800">
                {newChanType === 'direct' ? 'Start Private Chat' :
                  newChanType === 'group' ? 'New Group Chat' : 'Create Custom Channel'}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-slate-200 rounded text-slate-600">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleCreateChannel} className="p-4 flex flex-col gap-4">
              {['direct', 'group'].includes(newChanType) ? (
                <>
                  {newChanType === 'group' && (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-slate-600 uppercase">Group Name</label>
                      <input
                        type="text"
                        value={newChanName}
                        onChange={e => setNewChanName(e.target.value)}
                        placeholder="e.g. Project Alpha (Optional)"
                        className="px-3 py-2 border rounded text-sm outline-none focus:border-blue-500"
                      />
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold text-slate-600 uppercase">
                      {newChanType === 'direct' ? 'Select Workspace Member' : 'Select Group Members'}
                    </label>

                    {/* Member Search Bar */}
                    <input
                      type="text"
                      value={memberSearchQuery}
                      onChange={e => setMemberSearchQuery(e.target.value)}
                      placeholder="Search members..."
                      className="w-full px-3 py-1.5 border rounded-lg text-xs outline-none focus:border-blue-500 bg-slate-50"
                    />

                    <div className="max-h-48 overflow-y-auto border rounded divide-y">
                      {companyUsers
                        .filter(u => {
                          const nameStr = u.name || u.full_name || '';
                          return nameStr.toLowerCase().includes(memberSearchQuery.toLowerCase());
                        })
                        .map(u => (
                          <div
                            key={u.id}
                            onClick={() => toggleSelectUser(u.id)}
                            className={`p-2.5 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors ${selectedUserIds.includes(u.id) ? 'bg-blue-50/50' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <Avatar user={u} size={28} />
                              <div>
                                <div className="text-xs font-bold text-slate-800">{u.name || u.full_name}</div>
                                <div className="text-[10px] text-slate-500 capitalize">{u.role}</div>
                              </div>
                            </div>
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedUserIds.includes(u.id) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`}>
                              {selectedUserIds.includes(u.id) && <Check size={10} />}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-600 uppercase">Channel Name</label>
                    <input
                      type="text"
                      value={newChanName}
                      onChange={e => setNewChanName(e.target.value)}
                      placeholder="e.g. general-discussions"
                      required
                      className="px-3 py-2 border rounded text-sm outline-none focus:border-blue-500"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-600 uppercase">Channel Type</label>
                    <select
                      value={newChanType}
                      onChange={e => {
                        const type = e.target.value;
                        setNewChanType(type);
                        setSelectedUserIds([]);
                        setSelectedDepartmentId('');
                        setSelectedProjectId('');
                        if (type === 'company') {
                          setSelectedUserIds(companyUsers.map(u => u.id));
                        }
                      }}
                      className="px-3 py-2 border rounded text-sm outline-none focus:border-blue-500 bg-white"
                    >
                      <option value="company">Company Wide (All employees)</option>
                      <option value="announcement">Announcements (Only leaders/managers post)</option>
                      {/* <option value="department">Department Chat</option> */}
                      <option value="project">Project Chat</option>
                    </select>
                  </div>

                  {newChanType === 'department' && (
                    <div className="flex flex-col gap-1.5 animate-slide-down">
                      <label className="text-xs font-bold text-slate-600 uppercase">Select Department</label>
                      <select
                        value={selectedDepartmentId}
                        onChange={e => {
                          const deptId = e.target.value;
                          setSelectedDepartmentId(deptId);
                          const deptUsers = companyUsers.filter(u => u.department_id === deptId || u.departmentId === deptId).map(u => u.id);
                          setSelectedUserIds(deptUsers);
                        }}
                        className="px-3 py-2 border rounded text-sm outline-none focus:border-blue-500 bg-white"
                        required
                      >
                        <option value="">-- Choose Department --</option>
                        {companyDepartments.map(d => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {newChanType === 'project' && (
                    <div className="flex flex-col gap-1.5 animate-slide-down">
                      <label className="text-xs font-bold text-slate-600 uppercase">Select Project</label>
                      <select
                        value={selectedProjectId}
                        onChange={async (e) => {
                          const projId = e.target.value;
                          setSelectedProjectId(projId);
                          try {
                            const { data } = await api.get(`/projects/${projId}`);
                            const projUsers = (data.members || []).map(u => u.id);
                            setSelectedUserIds(projUsers);
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                        className="px-3 py-2 border rounded text-sm outline-none focus:border-blue-500 bg-white"
                        required
                      >
                        <option value="">-- Choose Project --</option>
                        {userProjects.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 mt-2">
                    <label className="text-xs font-bold text-slate-600 uppercase">
                      Select Channel Members (Manual Override)
                    </label>
                    <input
                      type="text"
                      value={memberSearchQuery}
                      onChange={e => setMemberSearchQuery(e.target.value)}
                      placeholder="Search workspace members..."
                      className="w-full px-3 py-1.5 border rounded-lg text-xs outline-none focus:border-blue-500 bg-slate-50"
                    />
                    <div className="max-h-32 overflow-y-auto border rounded divide-y bg-white">
                      {companyUsers
                        .filter(u => {
                          const nameStr = u.name || u.full_name || '';
                          return nameStr.toLowerCase().includes(memberSearchQuery.toLowerCase());
                        })
                        .map(u => (
                          <div
                            key={u.id}
                            onClick={() => toggleSelectUser(u.id)}
                            className={`p-2 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors ${selectedUserIds.includes(u.id) ? 'bg-blue-50/50' : ''}`}
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-[10px]">
                                {u.full_name?.slice(0, 2).toUpperCase() || 'U'}
                              </div>
                              <div>
                                <div className="text-[10px] font-bold text-slate-800">{u.name || u.full_name}</div>
                                <div className="text-[8px] text-slate-500 capitalize">{u.role}</div>
                              </div>
                            </div>
                            <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${selectedUserIds.includes(u.id) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`}>
                              {selectedUserIds.includes(u.id) && <Check size={8} />}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    newChanType === 'direct' ? (selectedUserIds.length === 0) :
                      newChanType === 'group' ? (selectedUserIds.length === 0) :
                        newChanType === 'department' ? (!selectedDepartmentId || !newChanName.trim()) :
                          newChanType === 'project' ? (!selectedProjectId || !newChanName.trim()) :
                            !newChanName.trim()
                  }
                  className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded transition-all disabled:opacity-50"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Convert Message to Task Modal */}
      {showConvertModal && convertMsg && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-fade-in" data-testid="convert-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden border">
            <div className="flex justify-between items-center p-4 border-b bg-slate-50">
              <h3 className="font-bold text-slate-800">Convert Chat to Task</h3>
              <button onClick={() => setShowConvertModal(false)} className="p-1 hover:bg-slate-200 rounded text-slate-600">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleConvertSubmit} className="p-4 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase">Select Target Project</label>
                <select
                  value={convertProjId}
                  onChange={e => setConvertProjId(e.target.value)}
                  required
                  className="px-3 py-2 border rounded text-sm outline-none focus:border-blue-500 bg-white"
                  data-testid="convert-project-select"
                >
                  <option value="">-- Choose Project --</option>
                  {userProjects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase">Select Project List</label>
                <select
                  value={convertListId}
                  onChange={e => setConvertListId(e.target.value)}
                  required
                  disabled={!convertLists.length}
                  className="px-3 py-2 border rounded text-sm outline-none focus:border-blue-500 bg-white"
                  data-testid="convert-list-select"
                >
                  <option value="">-- Choose List --</option>
                  {convertLists.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase">Task Title</label>
                <input
                  type="text"
                  value={convertTitle}
                  onChange={e => setConvertTitle(e.target.value)}
                  required
                  className="px-3 py-2 border rounded text-sm outline-none focus:border-blue-500"
                  data-testid="convert-title-input"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase">Task Description</label>
                <textarea
                  value={convertDesc}
                  onChange={e => setConvertDesc(e.target.value)}
                  rows={4}
                  className="px-3 py-2 border rounded text-sm outline-none focus:border-blue-500 font-mono text-xs"
                  data-testid="convert-desc-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowConvertModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={convertBusy || !convertProjId || !convertListId || !convertTitle.trim()}
                  className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded transition-all disabled:opacity-50"
                  data-testid="convert-submit-btn"
                >
                  {convertBusy ? 'Converting...' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Message Info Modal */}
      {showInfoModal && messageInfo && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border flex flex-col max-h-[85vh]">
            <div className="flex justify-between items-center p-4 border-b bg-slate-50 flex-shrink-0">
              <h3 className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                <Info size={16} className="text-blue-600" />
                Message Info
              </h3>
              <button onClick={() => setShowInfoModal(false)} className="p-1 hover:bg-slate-200 rounded text-slate-600">
                <X size={16} />
              </button>
            </div>

            <div className="p-4 overflow-y-auto space-y-5 flex-1 bg-slate-50">
              {/* Message Text Card */}
              <div className="bg-[#E2F0CB] border border-[#C2E0A4] rounded-xl p-3.5 shadow-sm text-slate-800 text-xs leading-relaxed max-w-[85%] ml-auto">
                <p className="whitespace-pre-wrap">{messageInfo.messageText}</p>
              </div>

              {/* Status Lists */}
              <div className="space-y-4">
                {/* Read By */}
                <div>
                  <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <CheckCheck size={12} className="text-[#34B7F1]" /> Read By ({messageInfo.read.length})
                  </h4>
                  <div className="bg-white border rounded-xl overflow-hidden divide-y">
                    {messageInfo.read.length === 0 ? (
                      <div className="p-3 text-center text-[10px] text-slate-400 font-bold">No one has read this message yet.</div>
                    ) : (
                      messageInfo.read.map(u => (
                        <div key={u.id} className="flex items-center justify-between p-2.5 hover:bg-slate-50 transition-colors">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-xs" style={{ background: u.avatar_color ? `${u.avatar_color}15` : '#f1f5f9', color: u.avatar_color || '#475569' }}>
                              {u.full_name?.slice(0, 2).toUpperCase() || 'U'}
                            </div>
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-slate-800 truncate">{u.full_name}</div>
                              <div className="text-[9px] text-slate-400 capitalize truncate">{u.role}</div>
                            </div>
                          </div>
                          {u.updated_at && (
                            <span className="text-[9px] text-slate-400 font-semibold">
                              {new Date(u.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Delivered To */}
                <div>
                  <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <CheckCheck size={12} className="text-slate-400" /> Delivered To ({messageInfo.delivered.length})
                  </h4>
                  <div className="bg-white border rounded-xl overflow-hidden divide-y">
                    {messageInfo.delivered.length === 0 ? (
                      <div className="p-3 text-center text-[10px] text-slate-400 font-bold">No one has received this message yet.</div>
                    ) : (
                      messageInfo.delivered.map(u => (
                        <div key={u.id} className="flex items-center justify-between p-2.5 hover:bg-slate-50 transition-colors">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-xs" style={{ background: u.avatar_color ? `${u.avatar_color}15` : '#f1f5f9', color: u.avatar_color || '#475569' }}>
                              {u.full_name?.slice(0, 2).toUpperCase() || 'U'}
                            </div>
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-slate-800 truncate">{u.full_name}</div>
                              <div className="text-[9px] text-slate-400 capitalize truncate">{u.role}</div>
                            </div>
                          </div>
                          <span className="text-[9px] text-slate-400 font-semibold">Delivered</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Not Delivered To */}
                <div>
                  <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Check size={12} className="text-slate-400" /> Not Delivered / Offline ({messageInfo.not_delivered.length})
                  </h4>
                  <div className="bg-white border rounded-xl overflow-hidden divide-y">
                    {messageInfo.not_delivered.length === 0 ? (
                      <div className="p-3 text-center text-[10px] text-slate-400 font-bold">All members have received this message.</div>
                    ) : (
                      messageInfo.not_delivered.map(u => (
                        <div key={u.id} className="flex items-center justify-between p-2.5 hover:bg-slate-50 transition-colors">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-xs" style={{ background: u.avatar_color ? `${u.avatar_color}15` : '#f1f5f9', color: u.avatar_color || '#475569' }}>
                              {u.full_name?.slice(0, 2).toUpperCase() || 'U'}
                            </div>
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-slate-800 truncate">{u.full_name}</div>
                              <div className="text-[9px] text-slate-400 capitalize truncate">{u.role}</div>
                            </div>
                          </div>
                          <span className="text-[9px] text-red-400 font-bold">Offline</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t bg-slate-50 flex justify-end flex-shrink-0">
              <button onClick={() => setShowInfoModal(false)} className="tm-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Members Modal */}
      {showMembersModal && activeChannel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border flex flex-col max-h-[85vh]">
            <div className="flex justify-between items-center p-4 border-b bg-slate-50 flex-shrink-0">
              <h3 className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                <Users size={16} className="text-blue-600" />
                Manage Members - {activeChannel.name}
              </h3>
              <button onClick={() => setShowMembersModal(false)} className="p-1 hover:bg-slate-200 rounded text-slate-600">
                <X size={16} />
              </button>
            </div>

            <div className="p-4 overflow-y-auto space-y-5 flex-1">
              {/* Add New Members Section */}
              <div>
                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Add New Members</h4>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search teammates..."
                      value={memberSearchQuery}
                      onChange={e => setMemberSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 border rounded-xl text-xs outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="mt-2 border rounded-xl max-h-40 overflow-y-auto divide-y">
                  {companyUsers
                    .filter(u => !channelMembers.some(m => m.id === u.id))
                    .filter(u => u.full_name?.toLowerCase().includes(memberSearchQuery.toLowerCase()) || u.email?.toLowerCase().includes(memberSearchQuery.toLowerCase()))
                    .map(u => {
                      const isSelected = selectedUserIds.includes(u.id);
                      return (
                        <div key={u.id} className="flex items-center justify-between p-2 hover:bg-slate-50 transition-colors">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs">
                              {u.full_name?.slice(0, 2).toUpperCase() || 'U'}
                            </div>
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-slate-800 truncate">{u.full_name}</div>
                              <div className="text-[9px] text-slate-400 truncate">{u.email}</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleSelectUser(u.id)}
                            className={`px-2.5 py-1 text-[10px] font-bold rounded-lg border transition-all ${isSelected
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'text-slate-600 bg-white hover:bg-slate-100 border-slate-300'
                              }`}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </button>
                        </div>
                      );
                    })}
                  {companyUsers.filter(u => !channelMembers.some(m => m.id === u.id)).length === 0 && (
                    <div className="text-[10px] text-slate-400 p-3 text-center italic">All workspace users are already members</div>
                  )}
                </div>

                {selectedUserIds.length > 0 && (
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        handleAddMembers(selectedUserIds);
                        setSelectedUserIds([]);
                      }}
                      className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold rounded-lg shadow-sm transition-all"
                    >
                      Add Selected ({selectedUserIds.length})
                    </button>
                  </div>
                )}
              </div>

              {/* Current Members Section */}
              <div>
                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Current Members ({channelMembers.length})</h4>
                <div className="border rounded-xl divide-y overflow-hidden">
                  {loadingMembers ? (
                    <div className="p-4 text-center text-xs text-slate-500 font-semibold">Loading members...</div>
                  ) : (
                    channelMembers.map(m => (
                      <div key={m.id} className="flex items-center justify-between p-2.5 bg-white hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xs shadow-sm">
                            {m.full_name?.slice(0, 2).toUpperCase() || 'U'}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-slate-800 truncate">{m.full_name}</div>
                            <div className="text-[9px] text-slate-500 truncate capitalize">{m.role}</div>
                          </div>
                        </div>
                        {/* Remove / Chat Buttons */}
                        {m.id !== user.id && (
                          <div className="flex gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleStartDirectMessage(m)}
                              className="text-[10px] font-bold text-blue-600 hover:text-blue-700 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                            >
                              Chat
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveMember(m.id)}
                              className="text-[10px] font-bold text-red-600 hover:text-red-700 px-2.5 py-1 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="p-4 border-t bg-slate-50 flex justify-end flex-shrink-0">
              <button
                onClick={() => setShowMembersModal(false)}
                className="px-4 py-2 text-xs font-bold text-slate-700 bg-slate-200 hover:bg-slate-300 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Channel Settings Modal */}
      {showEditChanModal && activeChannel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border">
            <div className="flex justify-between items-center p-4 border-b bg-slate-50">
              <h3 className="font-bold text-slate-800 text-sm">Channel Settings</h3>
              <button onClick={() => setShowEditChanModal(false)} className="p-1 hover:bg-slate-200 rounded text-slate-600">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleUpdateChannel} className="p-6 flex flex-col items-center gap-5">
              {/* Avatar Preview */}
              <div className="relative group">
                <div
                  className="w-24 h-24 rounded-full overflow-hidden border-2 border-slate-200 flex items-center justify-center text-3xl font-bold text-white shadow-sm relative bg-slate-100"
                  style={{
                    background: editChanAvatarUrl ? `url(${editChanAvatarUrl}) center/cover` : '#0079BF'
                  }}
                >
                  {!editChanAvatarUrl && (
                    <span className="text-3xl">
                      {activeChannel.type === 'announcement' ? '📢' : activeChannel.type === 'project' ? '📁' : '#'}
                    </span>
                  )}

                  {uploadingChanAvatar && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-xs font-semibold text-white">
                      Uploading...
                    </div>
                  )}
                </div>
              </div>

              {/* Photo Action Buttons */}
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={chanAvatarFileInputRef}
                  onChange={handleChanPhotoUpload}
                  className="hidden"
                  accept="image/*"
                />
                <button
                  type="button"
                  onClick={() => chanAvatarFileInputRef.current?.click()}
                  className="px-3 py-1.5 text-[11px] font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                >
                  Upload custom icon
                </button>
                {editChanAvatarUrl && (
                  <button
                    type="button"
                    onClick={() => setEditChanAvatarUrl('')}
                    className="px-3 py-1.5 text-[11px] font-bold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                  >
                    Remove custom icon
                  </button>
                )}
              </div>

              {/* Name Input */}
              <div className="w-full flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Channel Name</label>
                <input
                  type="text"
                  value={editChanName}
                  onChange={e => setEditChanName(e.target.value)}
                  placeholder="Channel name"
                  required
                  className="w-full px-3 py-2 border rounded-xl text-sm outline-none focus:border-blue-500"
                />
              </div>

              {/* Footer Buttons */}
              <div className="w-full flex justify-end gap-2 pt-4 border-t mt-2">
                <button
                  type="button"
                  onClick={() => setShowEditChanModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadingChanAvatar || !editChanName.trim()}
                  className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all disabled:opacity-50"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Summary Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-fade-in" data-testid="summary-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden border">
            <div className="flex justify-between items-center p-4 border-b bg-yellow-50">
              <h3 className="font-bold text-slate-800 flex items-center gap-1">
                <Sparkles size={16} className="text-yellow-600 animate-pulse" />
                <span>AI Channel Summary</span>
              </h3>
              <button onClick={() => setShowSummaryModal(false)} className="p-1 hover:bg-slate-200 rounded text-slate-600">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 flex flex-col gap-3">
              {extractedTasks.length === 0 && (
                <>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Discussion Summary & Action Items</p>
                  <div className="bg-slate-50 p-4 rounded border text-sm leading-relaxed text-slate-700 whitespace-pre-wrap font-mono">
                    {summaryText}
                  </div>
                  {summaryText && (
                    <button
                      onClick={handleExtractTasks}
                      disabled={extractingTasks}
                      className="w-full py-2 bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 text-yellow-800 text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                    >
                      <Sparkles size={13} className={extractingTasks ? 'animate-spin' : ''} />
                      {extractingTasks ? 'Extracting tasks...' : 'Extract Action Items as Board Tasks'}
                    </button>
                  )}
                  <div className="flex justify-end pt-3">
                    <button
                      onClick={() => setShowSummaryModal(false)}
                      className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded transition-all shadow-sm"
                    >
                      Got it
                    </button>
                  </div>
                </>
              )}

              {extractedTasks.length > 0 && (
                <div className="space-y-3 pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Select Draft Tasks</span>
                    <span className="text-[10px] text-slate-400 font-semibold">{selectedExtractedTasks.length} of {extractedTasks.length} selected</span>
                  </div>

                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {extractedTasks.map((t, idx) => {
                      const selected = selectedExtractedTasks.includes(idx);
                      return (
                        <div key={idx} className={`p-2 rounded-lg border text-xs flex gap-2.5 items-start transition-all cursor-pointer ${selected ? 'border-blue-200 bg-blue-50/20' : 'border-slate-100 hover:bg-slate-50'}`} onClick={() => {
                          if (selected) {
                            setSelectedExtractedTasks(prev => prev.filter(i => i !== idx));
                          } else {
                            setSelectedExtractedTasks(prev => [...prev, idx]);
                          }
                        }}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => { }}
                            className="mt-0.5 pointer-events-none"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-slate-800 block truncate">{t.title}</span>
                            <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">{t.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Target Board</label>
                    <select
                      value={targetTaskProject}
                      onChange={(e) => setTargetTaskProject(e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-slate-200 rounded text-xs outline-none bg-white font-medium text-slate-800"
                    >
                      {userProjects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setExtractedTasks([])}
                      className="flex-1 py-2 text-xs font-semibold text-slate-500 bg-slate-100 hover:bg-slate-200 rounded transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleCreateExtractedTasks}
                      className="flex-1 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded transition-all shadow-sm flex items-center justify-center gap-1"
                    >
                      Add to Board
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating Audio Huddle Panel */}
      {incomingCall && !inHuddle && (
        <div className="fixed top-6 right-6 bg-slate-900 text-white rounded-2xl shadow-2xl p-4 flex flex-col gap-3 w-72 z-50 border border-slate-700/50 animate-bounce" data-testid="incoming-call-panel">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center font-bold text-lg overflow-hidden shrink-0">
              {incomingCall.callerAvatar ? (
                <img src={incomingCall.callerAvatar} alt={incomingCall.callerName} className="w-full h-full object-cover" />
              ) : (
                (incomingCall.callerName || 'U').charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <div className="font-bold text-sm truncate">{incomingCall.callerName}</div>
              <div className="text-xs text-slate-400">is calling you...</div>
            </div>
          </div>
          <div className="flex gap-2 mt-1">
            <button
              onClick={() => {
                const chan = channels.find(c => c.id === incomingCall.channelId);
                if (chan) {
                  setActiveChannel(chan);
                  joinHuddle(chan);
                }
                setIncomingCall(null);
              }}
              className="flex-1 py-2 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5"
            >
              <Phone size={14} /> Accept
            </button>
            <button
              onClick={() => {
                if (socket) {
                  socket.emit('huddle:reject', { callerId: incomingCall.callerId, channelId: incomingCall.channelId });
                }
                setIncomingCall(null);
              }}
              className="flex-1 py-2 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5"
            >
              <PhoneOff size={14} /> Decline
            </button>
          </div>
        </div>
      )}

      {inHuddle && huddleActiveChannel && (
        <div className="fixed bottom-6 right-6 bg-slate-900/90 text-white rounded-2xl shadow-2xl p-4 flex flex-col gap-3 w-64 z-45 border border-slate-700/50 backdrop-blur-md animate-fade-in" data-testid="huddle-panel">
          <div className="flex items-center justify-between border-b border-slate-800 pb-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
              <span className="text-xs font-bold tracking-wide text-slate-300 uppercase">Huddle: #{huddleActiveChannel.name}</span>
            </div>
            <span className="text-[10px] bg-slate-800 text-slate-400 font-black px-1.5 py-0.5 rounded-full">
              {huddleParticipants.length + 1} online
            </span>
          </div>

          <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
            <div className="flex items-center justify-between text-xs bg-slate-800/40 p-1.5 rounded-lg border border-slate-800">
              <div className="flex items-center gap-2">
                <Avatar user={user} size={24} />
                <span className="font-semibold text-slate-200 truncate max-w-[120px]">{user.fullName || 'You'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {isMuted ? (
                  <MicOff size={12} className="text-red-400" />
                ) : (
                  <div className="flex gap-[2px] items-end h-3">
                    <div className="w-[2px] bg-emerald-400 animate-pulse h-2" />
                    <div className="w-[2px] bg-emerald-400 animate-pulse h-1" />
                    <div className="w-[2px] bg-emerald-400 animate-pulse h-3" />
                  </div>
                )}
                <span className="text-[9px] text-slate-500 uppercase font-black tracking-wider">You</span>
              </div>
            </div>

            {huddleParticipants.map((p, idx) => {
              const pUser = companyUsers.find(u => u.id === p.userId);
              return (
                <div key={idx} className="flex items-center justify-between text-xs bg-slate-800/20 p-1.5 rounded-lg border border-slate-900">
                  <div className="flex items-center gap-2">
                    <Avatar user={pUser || { fullName: 'Teammate' }} size={24} />
                    <span className="font-semibold text-slate-350 truncate max-w-[120px]">{pUser?.full_name || 'Teammate'}</span>
                  </div>
                  {p.isMuted ? (
                    <MicOff size={11} className="text-red-400" />
                  ) : (
                    <div className="flex gap-[2px] items-end h-3">
                      <div className="w-[2px] bg-blue-400 animate-pulse h-1" />
                      <div className="w-[2px] bg-blue-400 animate-pulse h-3" />
                      <div className="w-[2px] bg-blue-400 animate-pulse h-2" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex gap-2 pt-1.5 border-t border-slate-800">
            <button
              onClick={toggleMute}
              className={`flex-1 py-2 text-xs font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-sm ${isMuted ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-slate-800 hover:bg-slate-700 text-slate-200'}`}
              title={isMuted ? "Unmute Microphone" : "Mute Microphone"}
              data-testid="huddle-mute-btn"
            >
              {isMuted ? <MicOff size={13} /> : <Mic size={13} />}
              {isMuted ? 'Muted' : 'Mute'}
            </button>
            <button
              onClick={leaveHuddle}
              className="flex-1 py-2 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5"
              title="Leave Huddle"
              data-testid="huddle-leave-btn"
            >
              <PhoneOff size={13} />
              Leave
            </button>
          </div>

          {Object.entries(remoteStreams).map(([socketId, stream]) => (
            <audio
              key={socketId}
              autoPlay
              ref={el => {
                if (el && stream) el.srcObject = stream;
              }}
            />
          ))}
        </div>
      )}

      {/* Chat Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl w-[720px] h-[520px] flex flex-col overflow-hidden border border-slate-100" data-testid="chat-settings-modal">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between bg-slate-50 flex-shrink-0">
              <span className="font-bold text-base text-slate-800">Chat settings</span>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="p-1.5 hover:bg-slate-200 rounded-full text-slate-600 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Split Content */}
            <div className="flex-1 flex overflow-hidden">
              {/* Left Pane navigation */}
              <div className="w-56 bg-slate-50 border-r border-slate-100 p-3 space-y-1 overflow-y-auto flex-shrink-0">
                {[
                  { id: 'notifications', label: 'Notifications', icon: Bell },
                  { id: 'message_media', label: 'Message & Media', icon: MessageSquare },
                  { id: 'appearance', label: 'Appearance', icon: Palette },
                  { id: 'accessibility', label: 'Accessibility', icon: Sparkles },
                  { id: 'access_restrictions', label: 'Access Restrictions', icon: Lock },
                  { id: 'data_privacy', label: 'Data Privacy', icon: Shield }
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setSettingsTab(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-xs font-semibold transition-all ${settingsTab === item.id
                        ? 'bg-blue-50 text-blue-600 font-bold'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                        }`}
                    >
                      <Icon size={16} />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Right Content Pane */}
              <div className="flex-1 p-6 overflow-y-auto bg-slate-50/50">
                {settingsTab === 'notifications' && (
                  <div className="space-y-6">
                    {/* Desktop notifications */}
                    <div className="bg-white rounded-2xl p-4 border border-slate-100 space-y-4">
                      <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">Desktop notifications</h4>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-bold text-slate-800">Allow chat notifications</div>
                        </div>
                        <ToggleSwitch
                          checked={chatSettings.allowNotifications}
                          onChange={(val) => setChatSettings({ ...chatSettings, allowNotifications: val })}
                        />
                      </div>

                      {chatSettings.allowNotifications && browserNotificationPermission !== 'granted' && (
                        <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 flex items-start gap-3">
                          <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                          <div className="text-[11px] text-blue-800 leading-relaxed">
                            Notifications for Chat are turned on, but this browser is preventing them from being delivered. Google Chat requires browser permissions to show notifications on this device and prevent duplicate alerts on mobile.
                            <button
                              onClick={requestBrowserNotificationPermission}
                              className="ml-2 font-bold underline hover:text-blue-900"
                            >
                              Turn on
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Reaction notifications */}
                    <div className="bg-white rounded-2xl p-4 border border-slate-100 flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="text-xs font-bold text-slate-800">Reaction notifications</div>
                        <div className="text-[10px] text-slate-400">Enable notifications for reactions to your messages</div>
                      </div>
                      <ToggleSwitch
                        checked={chatSettings.allowReactions}
                        onChange={(val) => setChatSettings({ ...chatSettings, allowReactions: val })}
                      />
                    </div>

                    {/* Sounds */}
                    <div className="bg-white rounded-2xl p-4 border border-slate-100 space-y-3">
                      <div className="text-xs font-bold text-slate-800">Notification sounds</div>
                      <select
                        value={chatSettings.notificationSound}
                        onChange={(e) => setChatSettings({ ...chatSettings, notificationSound: e.target.value })}
                        className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl p-2.5 outline-none focus:border-blue-500 text-slate-700"
                      >
                        <option value="tones">Tones</option>
                        <option value="chime">Chime</option>
                        <option value="subtle">Subtle</option>
                        <option value="none">None</option>
                      </select>
                    </div>

                    {/* Email */}
                    <div className="bg-white rounded-2xl p-4 border border-slate-100 flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="text-xs font-bold text-slate-800">Email notifications</div>
                        <div className="text-[10px] text-slate-400">Get email notifications for unread direct messages or @mentions</div>
                      </div>
                      <ToggleSwitch
                        checked={chatSettings.emailNotifications}
                        onChange={(val) => setChatSettings({ ...chatSettings, emailNotifications: val })}
                      />
                    </div>
                  </div>
                )}

                {settingsTab === 'message_media' && (
                  <div className="space-y-4">
                    <div className="bg-white rounded-2xl p-4 border border-slate-100">
                      <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-4">Message Features</h4>
                      <div className="space-y-4">
                        {[
                          { key: 'smartCompose', label: 'Smart Compose', desc: 'Show proactive writing suggestions' },
                          { key: 'smartReply', label: 'Smart Reply', desc: 'Show suggested replies when available' },
                          { key: 'autoCorrect', label: 'Auto-correct', desc: 'Enable auto-correct when composing a message' },
                          { key: 'conversationSummary', label: 'Conversation Summary', desc: 'Show summaries in spaces with several unread messages' },
                          { key: 'liveMarkdown', label: 'Live Markdown', desc: 'Enable Markdown formatting while typing' }
                        ].map((feat) => (
                          <div key={feat.key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                            <div>
                              <div className="text-xs font-bold text-slate-800">{feat.label}</div>
                              <div className="text-[10px] text-slate-400">{feat.desc}</div>
                            </div>
                            <ToggleSwitch
                              checked={chatSettings[feat.key]}
                              onChange={(val) => setChatSettings({ ...chatSettings, [feat.key]: val })}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {settingsTab === 'appearance' && (
                  <div className="space-y-6">
                    {/* Accent Color Selection */}
                    <div className="bg-white rounded-2xl p-4 border border-slate-100 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-800">Color</span>
                        <span className="text-xs font-semibold text-blue-600">
                          {ACCENT_COLORS.find(c => c.id === chatSettings.accentColor)?.label || 'Sapphire'}
                        </span>
                      </div>

                      <div className="grid grid-cols-7 gap-2">
                        {ACCENT_COLORS.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setChatSettings({ ...chatSettings, accentColor: c.id })}
                            className={`w-10 h-10 rounded-xl flex items-center justify-center border-2 transition-all ${chatSettings.accentColor === c.id ? 'border-[#185abc] bg-slate-50' : 'border-transparent hover:border-slate-100'
                              }`}
                          >
                            <div className="w-7 h-7 rounded-full overflow-hidden flex flex-col border border-slate-100 rotate-45">
                              <div style={{ backgroundColor: c.color }} className="flex-1" />
                              <div style={{ backgroundColor: c.light }} className="flex-1" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Display Density */}
                    <div className="bg-white rounded-2xl p-4 border border-slate-100 space-y-4">
                      <div className="text-xs font-bold text-slate-800">Density</div>
                      <div className="grid grid-cols-2 gap-4">
                        {/* Comfortable Layout Preview */}
                        <div
                          onClick={() => setChatSettings({ ...chatSettings, density: 'comfortable' })}
                          className="flex flex-col gap-2 cursor-pointer animate-fade-in"
                        >
                          <div className={`p-4 rounded-2xl border-2 transition-all h-28 flex flex-col justify-center gap-3 ${chatSettings.density === 'comfortable' ? 'border-[#185abc] bg-blue-50/50' : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}>
                            <div className="flex gap-2.5 items-center">
                              <div className="w-6 h-6 rounded-full bg-blue-200" />
                              <div className="flex-1 space-y-1.5">
                                <div className="w-1/2 h-2 bg-blue-300 rounded animate-pulse" />
                                <div className="w-3/4 h-2 bg-blue-100 rounded" />
                              </div>
                            </div>
                            <div className="flex gap-2.5 items-center">
                              <div className="w-6 h-6 rounded-full bg-blue-200" />
                              <div className="flex-1 space-y-1.5">
                                <div className="w-1/3 h-2 bg-blue-300 rounded animate-pulse" />
                                <div className="w-2/3 h-2 bg-blue-100 rounded" />
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-xs font-bold justify-center select-none">
                            <input
                              type="radio"
                              checked={chatSettings.density === 'comfortable'}
                              onChange={() => setChatSettings({ ...chatSettings, density: 'comfortable' })}
                              className="text-blue-600 focus:ring-0"
                            />
                            <span>Comfortable</span>
                          </div>
                        </div>

                        {/* Compact Layout Preview */}
                        <div
                          onClick={() => setChatSettings({ ...chatSettings, density: 'compact' })}
                          className="flex flex-col gap-2 cursor-pointer animate-fade-in"
                        >
                          <div className={`p-4 rounded-2xl border-2 transition-all h-28 flex flex-col justify-center gap-1.5 ${chatSettings.density === 'compact' ? 'border-[#185abc] bg-blue-50/50' : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}>
                            <div className="space-y-1.5">
                              <div className="flex justify-between items-center">
                                <div className="w-1/3 h-1.5 bg-blue-300 rounded" />
                                <div className="w-1/2 h-1.5 bg-blue-100 rounded" />
                              </div>
                              <div className="w-full h-1.5 bg-blue-100 rounded" />
                            </div>
                            <div className="space-y-1.5">
                              <div className="flex justify-between items-center">
                                <div className="w-1/4 h-1.5 bg-blue-300 rounded" />
                                <div className="w-2/3 h-1.5 bg-blue-100 rounded" />
                              </div>
                              <div className="w-5/6 h-1.5 bg-blue-100 rounded" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-xs font-bold justify-center select-none">
                            <input
                              type="radio"
                              checked={chatSettings.density === 'compact'}
                              onChange={() => setChatSettings({ ...chatSettings, density: 'compact' })}
                              className="text-blue-600 focus:ring-0"
                            />
                            <span>Compact</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {['accessibility', 'access_restrictions', 'data_privacy'].includes(settingsTab) && (
                  <div className="bg-white rounded-2xl p-6 border border-slate-100 text-center space-y-2">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400 capitalize">
                      {settingsTab.replace(/_/g, ' ')}
                    </h4>
                    <p className="text-xs text-slate-500">Settings are fully active and managed at company-wide enterprise level.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

// -------------------------- Right Collapsible Task Side Panel --------------------------
function RightTaskPanel({ taskId, onClose, companyUsers = [] }) {
  const [activeTab, setActiveTab] = useState('details');
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');

  const loadData = async () => {
    try {
      const [tRes, cRes, aRes] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/comments/task/${taskId}`),
        api.get(`/activity-log/task/${taskId}`),
      ]);
      setTask(tRes.data.task);
      setComments(cRes.data.comments || []);
      setActivity(aRes.data.activity || []);
      setError(null);
    } catch (err) {
      console.error('Error loading task details:', err);
      setError('Failed to load task details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) {
      setLoading(true);
      loadData();
    }
  }, [taskId]);

  const updateTaskField = async (fields) => {
    try {
      const { data } = await api.patch(`/tasks/${taskId}`, fields);
      setTask((prev) => ({ ...prev, ...data.task }));
      loadData();
    } catch (err) {
      console.error('Error updating task field:', err);
      alert(err.response?.data?.error || 'Failed to update task');
    }
  };

  const toggleCheck = async (item) => {
    try {
      await api.patch(`/tasks/checklist/${item.id}`, { completed: !item.completed });
      loadData();
    } catch (err) {
      console.error('Error toggling checklist:', err);
      alert('Failed to update checklist item');
    }
  };

  const handleAddChecklistItem = async (e) => {
    e.preventDefault();
    if (!newChecklistItem.trim()) return;
    try {
      await api.post(`/tasks/${taskId}/checklist`, { title: newChecklistItem.trim() });
      setNewChecklistItem('');
      loadData();
    } catch (err) {
      console.error('Error adding checklist item:', err);
      alert('Failed to add checklist item');
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    try {
      await api.delete(`/tasks/checklist/${itemId}`);
      loadData();
    } catch (err) {
      console.error('Error deleting checklist item:', err);
      alert('Failed to delete checklist item');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await api.post(`/comments/task/${taskId}`, { body: newComment.trim() });
      setNewComment('');
      loadData();
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Failed to add comment');
    }
  };

  if (loading) {
    return (
      <div className="w-80 bg-white border-l flex flex-col h-full shadow-2xl items-center justify-center text-slate-400" style={{ borderColor: '#dfe1e6' }}>
        <span className="animate-pulse text-xs font-semibold uppercase tracking-wider">Loading Task...</span>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="w-80 bg-white border-l flex flex-col h-full shadow-2xl p-4" style={{ borderColor: '#dfe1e6' }}>
        <div className="flex items-center justify-between border-b pb-3 mb-4">
          <span className="font-bold text-xs uppercase text-slate-500 tracking-wider">Task Panel</span>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded text-slate-600">
            <X size={16} />
          </button>
        </div>
        <div className="text-center text-xs text-red-500 font-semibold">{error || 'Task not found'}</div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-white border-l flex flex-col h-full shadow-2xl relative" style={{ borderColor: '#dfe1e6' }}>
      {/* Header */}
      <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-yellow-500" />
          <span className="font-bold text-xs uppercase text-slate-500 tracking-wider font-sans">Task Panel</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded text-slate-600 transition-colors">
          <X size={16} />
        </button>
      </div>

      {/* Task Details Info & Inline Title Editor */}
      <div className="p-4 border-b flex flex-col gap-2">
        <input
          type="text"
          value={task.title}
          onChange={(e) => setTask({ ...task, title: e.target.value })}
          onBlur={() => updateTaskField({ title: task.title })}
          placeholder="Task title"
          className="font-bold text-sm text-slate-800 leading-snug border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none w-full px-1 py-0.5 rounded bg-transparent"
        />
        <div className="flex flex-wrap gap-2 mt-1 items-center">
          {/* Priority Dropdown */}
          <select
            value={task.priority_v2 || 'medium'}
            onChange={(e) => updateTaskField({ priority_v2: e.target.value })}
            className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase border-0 focus:ring-1 focus:ring-blue-500 ${task.priority_v2 === 'critical' ? 'bg-red-100 text-red-700' :
              task.priority_v2 === 'high' ? 'bg-orange-100 text-orange-700' :
                task.priority_v2 === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
              }`}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>

          {/* Status Dropdown */}
          <select
            value={task.task_status || 'new'}
            onChange={(e) => updateTaskField({ task_status: e.target.value })}
            className="text-[10px] font-bold px-2 py-0.5 bg-blue-100 text-blue-700 rounded uppercase border-0 focus:ring-1 focus:ring-blue-500"
          >
            <option value="new">New</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="review">Review</option>
            <option value="testing">Testing</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="text-xs text-slate-500 mt-2 space-y-2">
          {/* Assignee Picker */}
          <div className="flex items-center gap-1.5">
            <span>Assignee:</span>
            <select
              value={task.assignees?.[0]?.id || ''}
              onChange={(e) => updateTaskField({ assignee_ids: e.target.value ? [e.target.value] : [] })}
              className="text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded px-1.5 py-0.5 focus:outline-none"
            >
              <option value="">Unassigned</option>
              {companyUsers.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          </div>

          {/* Due Date Picker */}
          <div className="flex items-center gap-1.5">
            <span>Due Date:</span>
            <input
              type="date"
              value={task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : ''}
              onChange={(e) => updateTaskField({ due_date: e.target.value || null })}
              className="text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded px-1.5 py-0.5 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b text-xs font-semibold bg-slate-50">
        {[
          { id: 'details', label: 'Details' },
          { id: 'checklist', label: 'Checklist' },
          { id: 'comments', label: 'Comments' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2.5 text-center border-b-2 transition-all ${activeTab === tab.id ? 'border-blue-600 text-blue-600 bg-white' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Panels */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'details' && (
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase">Description</label>
              <textarea
                value={task.description || ''}
                onChange={(e) => setTask({ ...task, description: e.target.value })}
                onBlur={() => updateTaskField({ description: task.description })}
                placeholder="Enter task description..."
                rows={4}
                className="text-xs text-slate-600 mt-1 leading-relaxed border border-slate-200 hover:border-slate-300 focus:border-blue-500 focus:outline-none w-full p-1.5 rounded resize-none"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase">Activity Timeline</label>
              <div className="mt-2 space-y-3">
                {activity.length === 0 ? (
                  <div className="text-[10px] text-slate-400 italic">No activity logged yet.</div>
                ) : (
                  activity.map(act => {
                    const actorName = act.actor_name || 'System';
                    let text = `${actorName} ${act.action}`;
                    if (act.to_value) text += ` to "${act.to_value}"`;
                    return (
                      <div key={act.id} className="flex gap-2 text-[11px] text-slate-600 items-start">
                        <span className="text-blue-500">•</span>
                        <div>
                          <span>{text}</span>
                          <span className="text-[9px] text-slate-400 block">{new Date(act.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'checklist' && (
          <div className="space-y-3">
            <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Checklist Progress</label>
            <div className="space-y-2">
              {(!task.checklist || task.checklist.length === 0) ? (
                <div className="text-xs text-slate-400 italic">No checklist items yet.</div>
              ) : (
                task.checklist.map(item => (
                  <div key={item.id} className="flex items-center justify-between text-xs hover:bg-slate-50 p-1 rounded group">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={item.completed}
                        onChange={() => toggleCheck(item)}
                        className="rounded text-blue-600 outline-none"
                      />
                      <span className={item.completed ? 'line-through text-slate-400' : 'text-slate-700'}>{item.title}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteChecklistItem(item.id)}
                      className="text-slate-400 hover:text-red-500 hidden group-hover:block transition-colors"
                      title="Delete item"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Add Checklist Item Form */}
            <form onSubmit={handleAddChecklistItem} className="flex gap-1 pt-2 border-t mt-3">
              <input
                type="text"
                placeholder="Add checklist item..."
                value={newChecklistItem}
                onChange={e => setNewChecklistItem(e.target.value)}
                className="flex-1 px-2.5 py-1 border rounded text-xs outline-none focus:border-blue-500"
              />
              <button type="submit" className="px-2.5 py-1 bg-slate-800 text-white rounded text-xs font-bold hover:bg-slate-900 transition-colors">
                Add
              </button>
            </form>
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="flex flex-col h-full gap-3 justify-between">
            <div className="space-y-3 overflow-y-auto max-h-60">
              {comments.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No comments yet.</div>
              ) : (
                comments.map(c => (
                  <div key={c.id} className="p-2.5 rounded bg-slate-50 border border-slate-100 flex flex-col gap-1">
                    <div className="flex justify-between text-[10px] text-slate-500 font-bold">
                      <span>{c.user?.full_name || 'User'}</span>
                      <span>{new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p className="text-xs text-slate-700">{c.body}</p>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={handleAddComment} className="flex gap-1 pt-2 border-t mt-4">
              <input
                type="text"
                placeholder="Add a comment..."
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                className="flex-1 px-3 py-1.5 border rounded-full text-xs outline-none focus:border-blue-500"
              />
              <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white rounded-full text-xs font-bold hover:bg-blue-700 transition-colors">
                Post
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}


// -------------------------- Task Mention rich card components --------------------------
function TaskCardMention({ title, taskId }) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { setActiveTaskId } = useStore();

  const loadTask = async () => {
    if (task || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks`);
      const found = data.tasks.find(t => t.id === taskId);
      if (found) setTask(found);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <span
      className="inline-block relative cursor-pointer"
      onMouseEnter={() => { setHovered(true); loadTask(); }}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setActiveTaskId(taskId);
        }}
        className="text-blue-600 font-bold hover:underline bg-blue-50 px-1 py-0.5 rounded border border-blue-200 text-xs inline-flex items-center gap-1 mx-0.5 cursor-pointer font-sans"
      >
        📋 #{title}
      </button>
      {hovered && (
        <div className="absolute left-0 bottom-6 bg-white border border-slate-200 rounded shadow-xl p-3 z-50 w-64 text-slate-800 text-xs text-left">
          {loading ? (
            <div className="flex justify-center p-2"><div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
          ) : task ? (
            <div className="space-y-1.5">
              <div className="font-bold text-sm text-slate-800 truncate">{task.title}</div>
              <div className="flex justify-between text-[10px] text-slate-500">
                <span>Priority: <span className="font-semibold text-slate-700 capitalize">{task.priority}</span></span>
                <span>Status: <span className="font-semibold text-slate-700 capitalize">{task.task_status || 'new'}</span></span>
              </div>
              {task.due_date && (
                <div className="text-[10px] text-slate-500">
                  Due: <span className="font-semibold text-slate-700">{new Date(task.due_date).toLocaleDateString()}</span>
                </div>
              )}
              {task.assignees && task.assignees.length > 0 && (
                <div className="text-[10px] text-slate-500 truncate">
                  Assignees: <span className="font-semibold text-slate-700">{task.assignees.map(a => a.full_name).join(', ')}</span>
                </div>
              )}
            </div>
          ) : (
            <span className="text-slate-400">Failed to load task details</span>
          )}
        </div>
      )}
    </span>
  );
}

function parseMarkdown(text) {
  if (!text) return '';
  let escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  let html = escaped
    .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1 py-0.5 rounded text-[10px] font-mono text-pink-600">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(/~([^~]+)~/g, '<del>$1</del>');

  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function renderMessageText(text) {
  if (!text) return null;

  let settings = { liveMarkdown: true };
  try {
    const saved = localStorage.getItem('chat_settings');
    if (saved) settings = JSON.parse(saved);
  } catch (e) { }

  const taskRegex = /\[#([^\]]+)\]\(task:([a-f0-9-]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = taskRegex.exec(text)) !== null) {
    const matchIndex = match.index;
    if (matchIndex > lastIndex) {
      const sliceText = text.slice(lastIndex, matchIndex);
      parts.push(
        <span key={lastIndex}>
          {settings.liveMarkdown ? parseMarkdown(sliceText) : sliceText}
        </span>
      );
    }
    const title = match[1];
    const taskId = match[2];
    parts.push(<TaskCardMention key={matchIndex} title={title} taskId={taskId} />);
    lastIndex = taskRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    const sliceText = text.slice(lastIndex);
    parts.push(
      <span key={lastIndex}>
        {settings.liveMarkdown ? parseMarkdown(sliceText) : sliceText}
      </span>
    );
  }

  return parts.length > 0 ? parts : text;
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-colors duration-300 ${checked ? 'bg-blue-600' : 'bg-slate-300'
        }`}
    >
      <div
        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${checked ? 'translate-x-5' : 'translate-x-0'
          }`}
      />
    </button>
  );
}

function ThreadPanel({ message, onClose, currentUser, activeChannel }) {
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const repliesEndRef = useRef(null);

  const loadReplies = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/chat/messages/${message.id}/replies`);
      setReplies(data.replies || []);
    } catch (err) {
      console.error('Failed to load thread replies', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReplies();
  }, [message.id]);

  useEffect(() => {
    const handler = (e) => {
      const newReply = e.detail;
      setReplies(prev => {
        if (prev.some(r => r.id === newReply.id)) return prev;
        return [...prev, newReply];
      });
      setTimeout(() => {
        repliesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    };
    window.addEventListener(`chat:thread_reply:${message.id}`, handler);
    return () => window.removeEventListener(`chat:thread_reply:${message.id}`, handler);
  }, [message.id]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    try {
      await api.post(`/chat/channels/${message.channel_id}/messages`, {
        message_text: replyText.trim(),
        parent_id: message.id
      });
      setReplyText('');
    } catch (err) {
      alert('Failed to send reply');
    }
  };

  return (
    <div className="w-[380px] h-full bg-white border-l flex flex-col z-20 shadow-xl" style={{ borderColor: '#e0e0e0' }} data-testid="thread-panel">
      <div className="h-14 border-b px-4 flex justify-between items-center bg-slate-50 flex-shrink-0" style={{ borderColor: '#e0e0e0' }}>
        <div>
          <h3 className="text-xs font-black text-slate-800 leading-none">Thread</h3>
          <span className="text-[10px] text-slate-400 font-semibold mt-1 block">Replies to message</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600 transition-colors">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 relative">
          <div className="flex gap-2.5 items-start">
            <Avatar user={{ id: message.sender_id, full_name: message.sender_name, avatar_color: message.sender_color }} size={32} showStatus={false} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="font-bold text-xs text-slate-800">{message.sender_name}</span>
                <span className="text-[9px] text-slate-400 font-semibold">{new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <p className="text-xs text-slate-700 font-medium break-words leading-relaxed">{message.message_text}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="h-[1px] bg-slate-200 flex-1" />
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{replies.length} replies</span>
          <div className="h-[1px] bg-slate-200 flex-1" />
        </div>

        {loading ? (
          <div className="text-center py-6 text-xs text-slate-400">Loading replies...</div>
        ) : (
          <div className="space-y-4">
            {replies.map((reply) => (
              <div key={reply.id} className="flex gap-2.5 items-start">
                <Avatar user={{ id: reply.sender_id, full_name: reply.sender_name, avatar_color: reply.sender_color }} size={28} showStatus={false} />
                <div className="flex-1 min-w-0 bg-slate-50/50 p-2.5 rounded-xl border border-slate-100">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="font-bold text-xs text-slate-800">{reply.sender_name}</span>
                    <span className="text-[9px] text-slate-400 font-semibold">{new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <p className="text-xs text-slate-700 font-medium break-words leading-relaxed">{reply.message_text}</p>
                </div>
              </div>
            ))}
            <div ref={repliesEndRef} />
          </div>
        )}
      </div>

      <div className="p-3 border-t bg-slate-50 flex-shrink-0" style={{ borderColor: '#e0e0e0' }}>
        {activeChannel && activeChannel.is_member === false ? (
          <div className="text-center text-[10px] text-slate-400 font-semibold py-1">
            You cannot reply as you are no longer a member.
          </div>
        ) : (
          <form onSubmit={handleSendReply} className="flex gap-2 items-center">
          <input
            type="text"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Reply..."
            className="flex-1 px-3 py-2 border rounded-full text-xs bg-white outline-none focus:border-blue-500 transition-colors font-medium"
            data-testid="thread-reply-input"
          />
          <button
            type="submit"
            disabled={!replyText.trim()}
            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors disabled:opacity-40 flex items-center justify-center"
            title="Send reply"
            data-testid="thread-reply-send"
          >
            <Send size={12} />
          </button>
        </form>
        )}
      </div>
    </div>
  );
}

function ChannelDetailsPanel({ channel, members, pinnedMessages, messages, onClose, onUnpin }) {
  const [fileSearch, setFileSearch] = useState('');
  const [fileCategory, setFileCategory] = useState('all');

  const sharedFiles = useMemo(() => {
    return messages.filter(m => {
      if (!m.attachment_url) return false;

      // Filter by search query
      if (fileSearch.trim() && !m.attachment_name?.toLowerCase().includes(fileSearch.toLowerCase())) {
        return false;
      }

      // Filter by category
      const ext = m.attachment_url.split('.').pop()?.toLowerCase() || '';
      if (fileCategory === 'images') {
        return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
      }
      if (fileCategory === 'docs') {
        return ['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
      }
      if (fileCategory === 'others') {
        return !['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
      }

      return true;
    });
  }, [messages, fileSearch, fileCategory]);

  const downloadAllFiles = () => {
    if (sharedFiles.length === 0) return;
    sharedFiles.forEach((file) => {
      const link = document.createElement('a');
      link.href = file.attachment_url;
      link.download = file.attachment_name || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  return (
    <div className="w-[300px] h-full bg-white border-l flex flex-col z-20 shadow-xl" style={{ borderColor: '#e0e0e0' }} data-testid="channel-details-panel">
      <div className="h-14 border-b px-4 flex justify-between items-center bg-slate-50 flex-shrink-0" style={{ borderColor: '#e0e0e0' }}>
        <div>
          <h3 className="text-xs font-black text-slate-800 leading-none">Details</h3>
          <span className="text-[10px] text-slate-400 font-semibold mt-1 block">#{channel.name}</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600 transition-colors">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <div className="space-y-2">
          <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">About</h4>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 text-xs text-slate-600 space-y-1">
            <div><span className="font-bold">Type:</span> <span className="capitalize">{channel.type}</span></div>
            {channel.created_at && (
              <div><span className="font-bold">Created:</span> {new Date(channel.created_at).toLocaleDateString()}</div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-between">
            <span>📌 Pinned Messages</span>
            <span className="bg-slate-200 text-slate-700 px-1.5 py-0.2 rounded-full font-black scale-90">{pinnedMessages.length}</span>
          </h4>
          {pinnedMessages.length === 0 ? (
            <div className="text-[11px] text-slate-400 italic">No pinned messages</div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {pinnedMessages.map((pm) => (
                <div key={pm.id} className="p-2.5 bg-yellow-50/50 border border-yellow-100 rounded-lg text-xs flex flex-col gap-1 relative group">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-[10px] text-slate-700">{pm.sender_name}</span>
                    <button
                      onClick={() => onUnpin(pm.id)}
                      className="text-[9px] text-red-500 hover:underline hidden group-hover:block"
                    >
                      Unpin
                    </button>
                  </div>
                  <p className="text-slate-600 text-[11px] break-words">{pm.message_text}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-between w-full">
              <span>📁 Shared Files</span>
              <span className="bg-slate-200 text-slate-700 px-1.5 py-0.2 rounded-full font-black scale-90">{sharedFiles.length}</span>
            </h4>
            {sharedFiles.length > 0 && (
              <button
                onClick={downloadAllFiles}
                className="text-[10px] font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 flex-shrink-0"
                title="Download all currently filtered files"
                data-testid="download-all-btn"
              >
                <Download size={11} />
                Download All
              </button>
            )}
          </div>

          {/* Search box */}
          <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-lg px-2 py-1">
            <Search className="text-slate-400 mr-1.5 flex-shrink-0" size={12} />
            <input
              type="text"
              value={fileSearch}
              onChange={e => setFileSearch(e.target.value)}
              placeholder="Search files..."
              className="w-full bg-transparent border-none outline-none text-[11px] text-slate-800 placeholder-slate-400"
            />
            {fileSearch && (
              <X
                className="text-slate-400 cursor-pointer hover:text-slate-600 flex-shrink-0"
                size={12}
                onClick={() => setFileSearch('')}
              />
            )}
          </div>

          {/* Category Pills */}
          <div className="flex gap-1 overflow-x-auto pb-1">
            {[
              { id: 'all', label: 'All' },
              { id: 'images', label: 'Images' },
              { id: 'docs', label: 'Docs' },
              { id: 'others', label: 'Others' }
            ].map(cat => (
              <button
                key={cat.id}
                onClick={() => setFileCategory(cat.id)}
                className={`px-2 py-0.5 rounded text-[10px] font-bold border transition-all ${fileCategory === cat.id ? 'bg-blue-600 border-blue-600 text-white shadow-sm' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {sharedFiles.length === 0 ? (
            <div className="text-[11px] text-slate-400 italic">No matching files</div>
          ) : (
            <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
              {sharedFiles.map((sf) => (
                <a
                  key={sf.id}
                  href={sf.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col border border-slate-100 bg-slate-50 hover:bg-slate-100 rounded-lg overflow-hidden transition-colors"
                  title={sf.attachment_name}
                >
                  {sf.attachment_url.match(/\.(jpeg|jpg|gif|png|webp)/i) ? (
                    <img src={sf.attachment_url} alt="" className="h-16 object-cover w-full" />
                  ) : (
                    <div className="h-16 bg-slate-200 flex items-center justify-center text-slate-500 font-bold text-xs">📄</div>
                  )}
                  <div className="p-1 text-[9px] font-bold text-slate-700 truncate w-full">{sf.attachment_name}</div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

